import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CalendarDate, Triple } from '../../shared/types/triple.mts';
import { InstructionViewEvent } from '../../shared/types/instruction_view.mts';
import {
    createPostGameDriver,
    createMidGameDriver,
    createPreGameDriver,
    createFirstIncorrectTurnDriver,
    createAdvancedCarouselOptionsDriver,
    createShareIncentiveDriver,
} from './tutorial.tsx';
import './Game.css';
import SyncProblemsIcon from './assets/sync_problems.svg';
import CalendarIcon from './assets/calendar_month.svg';
import SettingsIcon from './assets/settings.svg';
import FlameIcon from './assets/flame.svg';
import ShareIcon from './assets/share.svg';
import InfoIcon from './assets/info.svg';
import PresentIcon from './assets/present.svg';
import SignalDisconnectedIcon from './assets/signal_disconnected.svg';
import KebabIcon from './assets/kebab_menu.svg';
import ResultsIcon from './assets/leaderboard.svg';
import PyramidLogo from './assets/icon_transparent.png';
import AppStoreButton from './assets/app_store_download.svg';
import PlayStoreButton from './assets/play_store_download.svg';
import PyramidLogoDark from './assets/icon_transparent_dark.png';
import { TZDate } from '@date-fns/tz';
import Modal from 'react-modal';
import { Switch } from './Switch.tsx';
import {
    Guess,
    GameState,
    scoreToCssClass,
    DifficultyLevel,
    TurnType,
    ArchiveSelector,
    SelectedPuzzle,
    BonusSelector,
    InternalSelector,
    DarkModeSetting,
    SpecialSelector,
    LoadingState,
    PuzzleState,
    isCompleted,
} from './State.tsx';
import {
    addSeconds,
    differenceInCalendarDays,
    differenceInHours,
    differenceInSeconds,
    isBefore,
    isFuture,
} from 'date-fns';
import { Histogram } from './Histogram.tsx';
import { CloseButton } from './CloseButton.tsx';
import { DateSelector } from './DateSelector.tsx';
import { v4 as uuidv4 } from 'uuid';
import Puzzle from './Puzzle.tsx';
import {
    API_BASE_URL,
    applyTimeout,
    dateToTimeString,
    flashNotification,
    formatTimeElapsed,
    getAppStoreWebLink,
    getStyleValue,
    getTrafficSource,
    isCandidateForApp,
    isIOS,
    isMobile,
    MODAL_CONTENT_DEFAULTS,
    MODAL_OVERLAY_DEFAULTS,
    runningAsNativeApp,
    TrafficSource,
    UserCancellationError,
} from './utils.ts';
import feedbackExample from './assets/feedback_example.jpg';
import { track } from './umami.ts';
import { TIME_ZONE, TUTORIAL_ID, ZERO_DAY } from './Constants.tsx';
import { ActionPerformed, LocalNotifications, LocalNotificationSchema } from '@capacitor/local-notifications';
import { InAppReview } from '@capacitor-community/in-app-review';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import { App, AppInfo } from '@capacitor/app';
import Spinner from './Spinner.tsx';
import { KeyValueStore } from './KeyValueStore.ts';
import { useKeyValueStore, useRawLocalState } from './GlobalState.tsx';
import * as Sentry from '@sentry/capacitor';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Dialog } from '@capacitor/dialog';
import { SyncStatus, usePuzzleIndex } from './PuzzleIndex.tsx';
import { SafeArea } from '@capacitor-community/safe-area';
import { StatusBar, Style } from '@capacitor/status-bar';
import { NavigationBar } from '@capgo/capacitor-navigation-bar';
import { useLoginSession } from './SessionContext.tsx';
import { PaywallTrigger, useSubscriptionContext } from './SubscriptionContext.tsx';
import { Account } from './Account.tsx';
import { useInGameNotificationContext } from './InGameNotificationContext.tsx';
import AnimatedSyncIcon from './SyncIcon.tsx';
import { useInactivityContext } from './InactivityContext.tsx';
import { VictoryModalCta } from './VictoryModalCta.tsx';
import { useAdInterfaceContext } from './AdInterface.tsx';
import { ArchiveRewardAdPromo } from './ArchiveRewardAdPromo.tsx';

const ARCHIVE_URL = '^/archive/(\\d{4})/(\\d{1,2})/(\\d{1,2})$';
const BONUS_URL = '^/bonus/(\\d+)$';
const BETA_URL = '/beta/((\\d+)|random)?$';
const SPECIAL_URL = '/special/(\\d+)?$';

const TOTAL_TURNS = 5;

const CHANNEL_ID = 'triplit-channel';
const NUM_BONUS_PUZZLES = 10;

function getAndUpdateLastRefresh() {
    let lastRefresh: TZDate;
    // If the page was navigated to via a reload, then update the last refresh.
    if ((performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload') {
        lastRefresh = new TZDate(new Date(), TIME_ZONE);
    } else {
        // Otherwise: use the value stored in local storage, else initialize it to the current time.
        const lastRefreshVal = window.localStorage.getItem('lastRefresh');
        lastRefresh =
            lastRefreshVal == null ? new TZDate(new Date(), TIME_ZONE) : new TZDate(lastRefreshVal, TIME_ZONE);
    }
    window.localStorage.setItem('lastRefresh', lastRefresh.toISOString());
    return lastRefresh;
}
const LAST_REFRESH = getAndUpdateLastRefresh();
const LAST_APP_PAUSE_TIME_KEY = 'lastAppPauseTime';

export default function Game() {
    // Context
    const store: KeyValueStore = useKeyValueStore();
    const { puzzleIndex, syncVersion, syncStatus } = usePuzzleIndex();
    const loginSession = useLoginSession();
    const getInitialSyncedState = () => puzzleIndex.getGameWideDataSnapshot(loginSession.getUserId());
    const initialLocalState = useRawLocalState();
    const subscriptionContext = useSubscriptionContext();
    const inGameNotificationsContext = useInGameNotificationContext();
    const { userIsActive } = useInactivityContext();
    const adInterface = useAdInterfaceContext();

    // Persisted global state
    const [isFirstVisit, setIsFirstVisit] = useState<boolean>(getInitialIsFirstVisit);
    const [seenArchiveRewardAdPromo, setSeenArchiveRewardAppPromo] = useState(() => {
        return (initialLocalState.seenArchiveRewardAdPromo ?? 'false') === 'true';
    });

    const [lastVisit, setLastVisit] = useState<Date>(new Date());
    const [lastVictoryDate, setLastVictoryDate] = useState<TZDate | undefined>(getInitialLastVictoryDate);
    const [streakLength, setStreakLength] = useState<number>(getInitialStreakLength);
    const [maxStreakLength, setMaxStreakLength] = useState<number>(getInitialMaxStreakLength);
    const [totalPlayed, setTotalPlayed] = useState<number>(getInitialTotalPlayed);
    const [totalWins, setTotalWins] = useState<number>(getInitialTotalWins);
    const [totalLosses, setTotalLosses] = useState<number>(getInitialTotalLosses);
    const [scoreDistribution, setScoreDistribution] = useState<object>(getInitialScoreDistribution);
    const [difficultyLevel, setDifficultyLevel] = useState<DifficultyLevel>(getInitialDifficultyLevel);
    const [timerEnabled, setTimerEnabled] = useState<boolean>(getInitialTimerEnabled);
    const [sessionId, setSessionId] = useState<string>(getInitialSessionId);
    const [consentGiven, setConsentGiven] = useState<boolean | undefined>(getInitialConsentGiven);
    const [userId, setUserId] = useState<string>(getInitialUserId);
    const [sessionIdCreationDate, setSessionIdCreationDate] = useState<Date>(getInitialSessionIdCreationDate);
    const [seenFeedbackTutorial, setSeenFeedbackTutorial] = useState<boolean>(getInitialSeenFeedbackTutorial);
    const [seenAdvancedCarouselOptions, setSeenAdvancedCarouselOptions] = useState<boolean>(
        getInitialSeenAdvancedCarouselOptions,
    );
    const [numBonusPuzzlesUnlocked, setNumBonusPuzzlesUnlocked] = useState<number>(getInitialNumBonusPuzzlesUnlocked);
    const [seenShareIncentive, setSeenShareIncentive] = useState<boolean>(getInitialSeenShareIncentive);
    const [seenNotificationsSettings, setSeenNotificationsSettings] = useState<boolean>(
        getInitialSeenNotificationsSettings,
    );
    const [seenNativeAppPromotion, setSeenNativeAppPromotion] = useState<boolean>(getInitialSeenNativeAppPromotion);
    const [bonusPuzzlesSeen, setBonusPuzzlesSeen] = useState<Set<number>>(getInitialBonusPuzzlesSeen);
    const [darkModeSetting, setDarkModeSetting] = useState<DarkModeSetting>(getInitialDarkModeSetting);
    const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(getInitialNotificationsEnabled);
    const [notificationTime, setNotificationTime] = useState<Date>(getInitialNotificationTime);
    const [askedForRating, setAskedForRating] = useState<boolean>(getInitialAskedForRating);
    const [askedForReview, setAskedForReview] = useState<boolean>(getInitialAskedForReview);

    // Puzzle state (that Game needs to know about but not persist)
    const [gameState, setGameState] = useState(GameState.UNKNOWN);
    const [guesses, setGuesses] = useState<Array<Guess>>([new Guess(4), new Guess(5), new Guess(6)]);
    const [numInstructionViews, setNumInstructionViews] = useState<number>(0);
    const [score, setScore] = useState<number | undefined>(undefined);
    const [timeElapsed, setTimeElapsed] = useState<number | undefined>(undefined);
    const [gameDifficultyLevel, setGameDifficultyLevel] = useState<DifficultyLevel | undefined>(undefined);

    // Non-persisted state
    const [firstRender, setFirstRender] = useState(true);
    const [notificationModalIsOpen, setNotificationModalIsOpen] = useState(false);
    const [nativeAppPromotionModalIsOpen, setNativeAppPromotionModalIsOpen] = useState(false);
    const [helpIsOpen, setHelpIsOpen] = useState(false);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [welcomeModalIsOpen, setWelcomeModalIsOpen] = useState(false);
    const [victoryModalIsOpen, setVictoryModalIsOpen] = useState(false);
    const [settingsModalIsOpen, setSettingsModalIsOpen] = useState(false);
    const [bonusPuzzlesModalIsOpen, setBonusPuzzlesModalIsOpen] = useState(false);
    const [agreementExamplesIsOpen, setAgreementExamplesIsOpen] = useState(false);
    const [wordplayExamplesIsOpen, setWordplayExamplesIsOpen] = useState(false);
    const [victoryMessagePopUpVisible, setVictoryMessagePopUpVisible] = useState(false);
    const [clipboardMessagePopUpVisible, setClipboardMessagePopUpVisible] = useState(false);
    const [difficultyLevelPopUpVisible, setDifficultyLevelPopUpVisible] = useState(false);
    const [settingsReviewPopUpVisible, setSettingsReviewPopUpVisible] = useState(false);
    const [accessingLockedBonusPuzzlePopUpVisible, setAccessingLockedBonusPuzzlePopUpVisible] = useState(false);
    const [accessingInvalidDatePopUpVisible, setAccessingInvalidDatePopUpVisible] = useState(false);
    const [notificationsRescheduledPopUpVisible, setNotificationsRescheduledPopUpVisible] = useState(false);
    const [invalidNotificationTimePopUpVisible, setInvalidNotificationTimePopUpVisible] = useState(false);
    const [permissionForNotificationsDeniedVisible, setPermissionForNotificationsDeniedVisible] = useState(false);
    const [accountUpgradeCanceledPopUpVisible, setAccountUpgradeCanceledPopUpVisible] = useState(false);
    const [bonusPuzzleUnlockedPopUpVisible, setBonusPuzzleUnlockedPopupVisible] = useState(false);
    const [archivedPuzzleAccessBlockedPopUpVisible, setArchivedPuzzleAccessBlockedPopUpVisible] = useState(false);
    const [dateSelectorOpen, setDateSelectorOpen] = useState(false);
    const [inactivityModalIsOpen, setInactivityModalIsOpen] = useState(false);
    const [selectedPuzzle, setSelectedPuzzle] = useState<SelectedPuzzle>(getInitialSelectedPuzzle);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [tutorialMode, setTutorialMode] = useState(false);
    const [practiceRoundCompleteModalIsOpen, setPracticeRoundCompleteModalIsOpen] = useState(false);
    const [shareData, setShareData] = useState<ShareData | undefined>(undefined);
    const [tutorialOnScreen, setTutorialOnScreen] = useState(false);
    const [selectedDifficultyLevel, setSelectedDifficultyLevel] = useState<DifficultyLevel>(getInitialDifficultyLevel);
    const [privacySettingsIsOpen, setPrivacySettingsIsOpen] = useState(false);
    const [darkMode, setDarkMode] = useState<boolean>(false);
    const [isGdpr, setIsGdpr] = useState<boolean>(true);
    const [onboardingExperiencedPlayer, setOnboardingExperiencedPlayer] = useState<boolean>(false);
    const [appInfo, setAppInfo] = useState<AppInfo | undefined>(undefined);
    const [localPersistenceIssues, setLocalPersistenceIssues] = useState<boolean>(false);
    const [badgeLegendIsOpen, setBadgeLegendIsOpen] = useState<boolean>(false);
    const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
    const [activeTriplitId, setActiveTriplitId] = useState<string | undefined>(undefined);
    const [activeUserId, setActiveUserId] = useState<string | undefined>(loginSession.getUserId());
    const [accountModalIsOpen, setAccountModalIsOpen] = useState<boolean>(false);
    const [victoryModalOpenCount, setVictoryModalOpenCount] = useState<number>(0);
    const [shouldShowArchiveRewardAdPromo, setShouldShowArchiveRewardAdPromo] = useState<boolean>(false);

    const MODAL_STATES = [
        notificationModalIsOpen,
        nativeAppPromotionModalIsOpen,
        welcomeModalIsOpen,
        bonusPuzzlesModalIsOpen,
        victoryModalIsOpen,
        settingsModalIsOpen,
        menuIsOpen,
        helpIsOpen,
        dateSelectorOpen,
        privacySettingsIsOpen,
        loginSession.isOpen,
        subscriptionContext.paywallTrigger,
        inactivityModalIsOpen,
        subscriptionContext.paywallIsOpen,
    ];

    // Synced refs (refs synced with state; these are read-only)
    const guessesSyncedRef = useRef(guesses);
    useEffect(() => {
        guessesSyncedRef.current = guesses;
    }, [guesses]);

    // Refs
    const instructionsOpenInstant = useRef<TZDate | null>(null);
    const isInitialRender = useRef<boolean>(true);

    function persistDifficultyLevel() {
        void puzzleIndex.setGameWideField('difficultyLevel', difficultyLevel, loginSession.getUserId());
    }

    function getBuildHash() {
        // Somewhat fragile, but it at least does not depend on the order of script tags, and the name format
        // should be pretty stable over time.
        const scripts = document.getElementsByTagName('script');
        return (
            Array.from(scripts)
                .find((script) => script.src.includes('/static/main'))
                ?.src.split('/')
                .pop()
                ?.split('.')[1] ?? 'unknown'
        );
    }

    function getFeedbackEmailBody() {
        const puzzleState = activeTriplitId
            ? puzzleIndex.getPuzzleStateById(activeTriplitId, loginSession.getUserId())
            : undefined;
        return encodeURIComponent(
            [
                `Diagnostic Info (please do not modify):`,
                `userId: ${loginSession.getUserId()}`,
                `triplitId: ${activeTriplitId ?? undefined}`,
                `platform: ${Capacitor.getPlatform()}`,
                `userAgent: ${navigator.userAgent}`,
                `location: ${window.location.href}`,
                `environment: ${window.ENVIRONMENT_NAME}`,
                `client-version: ${window.CLIENT_VERSION}`,
                `native-version: ${appInfo?.version ?? 'N/A'}`,
                `native-build: ${appInfo?.build ?? 'N/A'}`,
                `app-info: ${appInfo ? JSON.stringify(appInfo, null, 2) : 'n/a'}`,
                `build: ${getBuildHash()}`,
                `gameState: ${gameState}`,
                `puzzleState: ${JSON.stringify(puzzleState)}`,
                `=============================\n\nComments:\n\n\n`,
                `Please feel free to attach a screenshot of the issue you are experiencing!`,
            ].join('\n'),
        );
    }

    function getEmailFeedbackURL() {
        return `mailto:feedback@triplit.com?subject=${encodeURIComponent(
            'Triplit Feedback',
        )}&body=${getFeedbackEmailBody()}`;
    }

    function openBadgeLegendModal() {
        setBadgeLegendIsOpen(true);
    }

    function closeBadgeLegendModal() {
        setBadgeLegendIsOpen(false);
    }

    function openAgreementExamplesModal() {
        setAgreementExamplesIsOpen(true);
    }

    function closeAgreementExamplesModal() {
        setAgreementExamplesIsOpen(false);
    }

    function openWordplayExamplesModal() {
        setWordplayExamplesIsOpen(true);
    }

    function closeWordplayExamplesModal() {
        setWordplayExamplesIsOpen(false);
    }

    function openHelpModal() {
        setNumInstructionViews(numInstructionViews + 1);
        setHelpIsOpen(true);
    }

    function afterOpenHelpModal() {
        instructionsOpenInstant.current = new TZDate(new Date(), TIME_ZONE);
    }

    function closeHelpModal() {
        const durationOpen = differenceInSeconds(
            new TZDate(new Date(), TIME_ZONE),
            instructionsOpenInstant.current ?? new TZDate(new Date(), TIME_ZONE),
        );
        fetch(`${API_BASE_URL}/api/v1/event/instruction_view`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(InstructionViewEvent.from({ sessionId, duration: durationOpen })),
        }).catch((e) => {
            console.log('Error sending instruction view event:', e);
        });
        setHelpIsOpen(false);
    }

    function openMenuModal() {
        setMenuIsOpen(true);
    }

    function closeMenuModal() {
        setMenuIsOpen(false);
    }

    function openSettingsModal() {
        setSettingsModalIsOpen(true);
    }

    function closeSettingsModal() {
        setDifficultyLevel(selectedDifficultyLevel);
        if (selectedDifficultyLevel !== gameDifficultyLevel) {
            track('difficulty_level_adjust', getCustomEventData({ difficulty_level: selectedDifficultyLevel }));
            if (
                (selectedDifficultyLevel > gameDifficultyLevel! || isCompleted(gameState)) &&
                !onboardingExperiencedPlayer
            ) {
                // Notify the player that the change will take effect on the next puzzle.
                flashNotification(setDifficultyLevelPopUpVisible, 3000);
            }
        }
        setSettingsModalIsOpen(false);
    }

    function openWelcomeModal() {
        setWelcomeModalIsOpen(true);
    }

    function closeWelcomeModal() {
        setWelcomeModalIsOpen(false);
    }

    function openNotificationModal() {
        setSeenNotificationsSettings(true);
        setNotificationModalIsOpen(true);
    }

    function openNativeAppPromotionModal() {
        setSeenNativeAppPromotion(true);
        setNativeAppPromotionModalIsOpen(true);
    }

    function closeNativeAppPromotionModal() {
        setNativeAppPromotionModalIsOpen(false);
        openResultsModal();
    }

    function closeNotificationModal() {
        openResultsModal();
        setNotificationModalIsOpen(false);
    }

    function openBonusPuzzlesModal() {
        setBonusPuzzlesModalIsOpen(true);
    }

    function closeBonusPuzzlesModal() {
        setBonusPuzzlesModalIsOpen(false);
    }

    function openResultsModal() {
        setVictoryModalIsOpen(true);
    }

    function closeVictoryModal() {
        setVictoryModalOpenCount((count) => count + 1);
        setVictoryModalIsOpen(false);
    }

    function openDateSelectorModal() {
        setDateSelectorOpen(true);
    }

    function closeDateSelectorModal() {
        setDateSelectorOpen(false);
    }

    function openPracticeTriplitCompleteModal() {
        setTutorialOnScreen(false);
        setPracticeRoundCompleteModalIsOpen(true);
    }

    function closePracticeRoundCompleteModal() {
        setTutorialMode(false);
        setPracticeRoundCompleteModalIsOpen(false);
    }

    function openPrivacySettings() {
        // If the user has not yet explicitly given or refused consent,
        // when they open the privacy settings modal for the first time,
        // we assume default consent is refused in GDPR regions, and consent given
        // everywhere else. The user can then override this default
        // in the modal.
        setConsentGiven((consentGiven) => {
            return consentGiven ?? !isGdpr;
        });
        setPrivacySettingsIsOpen(true);
    }

    function closePrivacySettings() {
        setPrivacySettingsIsOpen(false);
    }

    function openInactivityModal() {
        setInactivityModalIsOpen(true);
    }

    function closeInactivityModal() {
        setInactivityModalIsOpen(false);
    }

    function getCustomEventData(data = {}) {
        return Object.assign(
            {
                newUser: isFirstVisit,
            },
            data,
        );
    }

    function startMidGameDriver() {
        if (!tutorialMode) {
            return;
        }
        track('tutorial_first_word_entered', getCustomEventData());
        setTutorialOnScreen(true);
        createMidGameDriver(() => {
            setTutorialOnScreen(false);
        }).drive();
    }

    function startFirstIncorrectTurnDriver(turnType: TurnType, correctParialSubmit: boolean) {
        if (seenFeedbackTutorial || !canLaunchTutorial()) {
            return;
        }
        setSeenFeedbackTutorial(true);
        setTutorialOnScreen(true);
        createFirstIncorrectTurnDriver(turnType, correctParialSubmit, () => {
            setTutorialOnScreen(false);
        }).drive();
    }

    function onAlreadyCompleted(score: number, shareData: ShareData) {
        setScore(score);
        setShareData(shareData);
    }

    function onWin(score: number, shareData: ShareData) {
        if (tutorialMode) {
            setTutorialOnScreen(true);
            const postGameDriver = createPostGameDriver(score, openPracticeTriplitCompleteModal);
            postGameDriver.drive();
            return;
        }
        void puzzleIndex.recordWin(score, loginSession.getUserId());
        setGameState(GameState.WON);
        setShareData(shareData);
        setScore(score);
        setScoreDistribution((scoreDistribution) => {
            const newScoreDistribution = Object.assign({}, scoreDistribution);
            newScoreDistribution[score] = (newScoreDistribution[score] || 0) + 1;
            return newScoreDistribution;
        });
        setTotalWins((totalWins) => totalWins + 1);
        handleNonTutorialGameCompletion(GameState.WON);
    }

    function onLoss(score: number, imageAccolade: ShareData) {
        if (activeTriplitId === TUTORIAL_ID) {
            const postGameDriver = createPostGameDriver(score, openPracticeTriplitCompleteModal);
            postGameDriver.drive();
            return;
        }
        void puzzleIndex.recordLoss(loginSession.getUserId());
        setGameState(GameState.LOST);
        setShareData(imageAccolade);
        setScore(-1);
        setTotalLosses((totalLosses) => totalLosses + 1);
        handleNonTutorialGameCompletion(GameState.LOST);
    }

    async function cancelTodaysNotification() {
        // This notification should not be rescheduled since we only schedule
        // notifications for future dates.
        if (!runningAsNativeApp()) {
            return;
        }
        const idForToday = formatIdForDate(new Date());
        try {
            const existing = await LocalNotifications.getPending();
            existing.notifications
                .filter((notification) => notification.id === idForToday)
                .forEach((notification) => {
                    console.log('Found notification for today, canceling it: ', notification);
                    void LocalNotifications.cancel({ notifications: [notification] });
                });
        } catch (e) {
            console.error('Error cancelling notification:', e);
        }
    }

    function showPrompt(message: string) {
        return Dialog.confirm({
            message,
            okButtonTitle: 'Yes',
            cancelButtonTitle: 'No',
        });
    }

    function promptUnhappyUserForFeedback() {
        void showPrompt('We’d love to hear how we can make Triplit better for you! Send feedback?').then((result) => {
            if (result.value) {
                window.location.href = getEmailFeedbackURL();
            }
        });
    }

    function promptForRatingOrReview(updatedGameState: GameState, updatedStreakLength: number) {
        if (updatedGameState === GameState.WON && runningAsNativeApp()) {
            if (updatedStreakLength >= 3 && updatedStreakLength < 7 && !askedForRating) {
                setAskedForRating(true);
                void showPrompt('Liking Triplit so far?').then((result) => {
                    if (result.value) {
                        track('enjoying_triplit_yes', getCustomEventData());
                        void showPrompt('That means a lot. Would you leave us a quick rating? It really helps!').then(
                            (result) => {
                                if (result.value) {
                                    track('willing_to_rate', getCustomEventData());
                                    void InAppReview.requestReview();
                                } else {
                                    track('enjoying_but_not_willing_to_rate', getCustomEventData());
                                }
                            },
                        );
                    } else {
                        track('enjoying_triplit_no', getCustomEventData());
                        promptUnhappyUserForFeedback();
                    }
                });
            } else if (updatedStreakLength >= 7 && !askedForReview) {
                setAskedForReview(true);
                void showPrompt("You're on an incredible roll! Still enjoying Triplit?").then((result) => {
                    if (result.value) {
                        track('enjoying_triplit_veteran_yes', getCustomEventData());
                        void showPrompt("We're so grateful you've been playing! Want to leave a short review?").then(
                            (result) => {
                                if (result.value) {
                                    track('willing_to_review_yes', getCustomEventData());
                                    void Browser.open({ url: getAppStoreWebLink(/* reviewPrompt= */ true)! });
                                } else {
                                    track('enjoying_but_not_willing_to_review', getCustomEventData());
                                }
                            },
                        );
                    } else {
                        track('enjoying_triplit_veteran_no', getCustomEventData());
                        promptUnhappyUserForFeedback();
                    }
                });
            }
        }
    }

    function handleNonTutorialGameCompletion(updatedGameState: GameState) {
        if (selectedPuzzle instanceof ArchiveSelector && selectedPuzzle.isToday()) {
            console.log("User completed today's puzzle; deleting pending notification for today if it exists.");
            // The user already completed today's puzzle, so there's no need to notify them to play today.
            void cancelTodaysNotification();
        }
        setVictoryMessagePopUpVisible(true);
        const victoryTimeLocal = Date.now();
        const victoryTime = new TZDate(victoryTimeLocal, TIME_ZONE);
        const updatedStreakLength = updateStreak(victoryTime);
        setTotalPlayed((totalPlayed) => totalPlayed + 1);
        // Start preparing the interstitial ad right away, right when we show the victory message.
        void adInterface.prepareCompletionInterstitialAdIfNeeded().catch((e) => {
            console.error('Error preparing interstitial ad:', e);
            Sentry.captureException(e);
        });
        setTimeout(async () => {
            setVictoryMessagePopUpVisible(false);
            if (!subscriptionContext.pro()) {
                let adResolved: boolean = false;
                try {
                    // Give the ad a bit of time to load. If it's longer than 2 seconds, show a loading spinner.
                    setTimeout(() => {
                        // Show the loading state if the ad is taking a while to load
                        if (!adResolved) {
                            setLoadingState(LoadingState.LOADING);
                        }
                    }, 2000);
                    await applyTimeout(
                        adInterface.prepareCompletionInterstitialAdIfNeeded().then(() => {
                            adResolved = true;
                        }),
                        3000,
                    );
                    // Do this outside of the timeout, otherwise the length of the video itself will count
                    // against the timeout.
                    await adInterface.showCompletionInterstitialAd();
                } catch (e) {
                    // Just move on and quietly log what happened
                    console.error('Error showing interstitial ad:', e);
                    Sentry.captureException(e);
                } finally {
                    adResolved = true;
                    setLoadingState(LoadingState.LOADED);
                }
            }
            if (
                runningAsNativeApp() &&
                updatedGameState === GameState.WON &&
                !seenNotificationsSettings &&
                !notificationsEnabled
            ) {
                openNotificationModal();
            } else if (isCandidateForApp() && updatedGameState === GameState.WON && !seenNativeAppPromotion) {
                openNativeAppPromotionModal();
            } else {
                openResultsModal();
                setTimeout(() => {
                    promptForRatingOrReview(updatedGameState, updatedStreakLength);
                }, 1200);
            }
        }, 1500);
    }

    function getWordingForScore() {
        if (score === 1) {
            return 'Perfect!';
        } else if (score === 2) {
            return 'Impressive';
        } else if (score === 3) {
            return 'Nice';
        } else if (score === 4) {
            return 'Solid';
        } else if (score === 5) {
            return 'Just made it';
        } else {
            return 'Maybe next time!';
        }
    }

    function updateStreak(victoryTime: TZDate) {
        let newStreakLength: number;
        if (lastVictoryDate != null) {
            const daysSinceLastVictory = differenceInCalendarDays(victoryTime, lastVictoryDate);
            if (daysSinceLastVictory === 1) {
                // The user extended their streak by a day.
                newStreakLength = streakLength + 1;
            } else if (daysSinceLastVictory === 0) {
                // If some how the user has correctly submitted multiple times on the same day, keep their streak the same.
                newStreakLength = streakLength;
            } else {
                // The current streak is stale. Start a new streak.
                newStreakLength = 1;
            }
        } else {
            // Start a new streak.
            newStreakLength = 1;
        }
        setStreakLength(newStreakLength);
        const newMaxStreakLength = Math.max(maxStreakLength, newStreakLength);
        setLastVictoryDate(victoryTime);
        void puzzleIndex.updateStreak(victoryTime, newStreakLength, loginSession.getUserId());
        setMaxStreakLength(newMaxStreakLength);
        return newStreakLength;
    }

    function autoLaunchTutorials() {
        const params = new URLSearchParams(window.location.search);
        return !['1', 'true'].includes(params.get('skipTutorial') ?? '');
    }

    function canLaunchTutorial() {
        // Either we are allowed to auto-launch tutorials, or the user has explicitly requested
        // the tutorial.
        return autoLaunchTutorials() || tutorialMode;
    }

    function getInitialIsFirstVisit(): boolean {
        // Default to true, since this will be set for returning players
        return initialLocalState.isFirstVisit !== 'false';
    }

    function getInitialStreakLength() {
        const lastVictoryDateRecalc = getInitialLastVictoryDate();
        const previousStreak = getInitialSyncedState().streakLength?.getValue() ?? 0;
        if (
            lastVictoryDateRecalc == null ||
            differenceInCalendarDays(new TZDate(Date.now(), TIME_ZONE), lastVictoryDateRecalc) > 1
        ) {
            return 0;
        } else {
            // The streak is still valid
            return previousStreak;
        }
    }

    function getInitialLastVictoryDate(): TZDate | undefined {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.lastVictory && new TZDate(initialSyncedState.lastVictory.getValue(), TIME_ZONE);
    }

    function getInitialMaxStreakLength(): number {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.maxStreakLength?.getValue() ?? 0;
    }

    function getInitialTotalPlayed(): number {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.totalPlayed?.getValue() ?? 0;
    }

    function getInitialTotalWins(): number {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.totalWins?.getValue() ?? 0;
    }

    function getInitialTotalLosses(): number {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.totalLosses?.getValue() ?? 0;
    }

    function getInitialScoreDistribution(): object {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.scoreDistribution?.getValue() ?? {};
    }

    function getInitialDifficultyLevel(): DifficultyLevel {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.difficultyLevel?.getValue() ?? DifficultyLevel.NORMAL;
    }

    function getInitialTimerEnabled() {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.timerEnabled?.getValue() ?? false;
    }

    function getInitialSessionId() {
        const initialSyncedState = getInitialSyncedState();
        const sessionIdCreationDateVal = initialSyncedState.sessionIdCreationDate;
        if (sessionIdCreationDateVal == null) {
            return uuidv4();
        } else {
            const sessionIdCreationDate = new TZDate(sessionIdCreationDateVal.getValue(), TIME_ZONE);
            const now = new TZDate(new Date(), TIME_ZONE);
            if (differenceInCalendarDays(now, sessionIdCreationDate) >= 1) {
                // The old session id has expired. Create a new id.
                return uuidv4();
            } else {
                return initialSyncedState.sessionId?.getValue() || uuidv4();
            }
        }
    }

    function getInitialSessionIdCreationDate() {
        const initialSyncedState = getInitialSyncedState();
        const sessionIdCreationDateVal = initialSyncedState.sessionIdCreationDate;
        const now = new TZDate(new Date(), TIME_ZONE);
        if (sessionIdCreationDateVal == null) {
            // A new session id will be generated now.
            return now;
        } else {
            const sessionIdCreationDate = new TZDate(sessionIdCreationDateVal.getValue(), TIME_ZONE);
            if (differenceInCalendarDays(now, sessionIdCreationDate) >= 1) {
                return now;
            } else {
                return new TZDate(sessionIdCreationDate, TIME_ZONE);
            }
        }
    }

    function getInitialConsentGiven(): boolean {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.consentGiven?.getValue() ?? true;
    }

    function getInitialUserId(): string {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.userId?.getValue() ?? uuidv4();
    }

    function getInitialSeenFeedbackTutorial(): boolean {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.seenFeedbackTutorial?.getValue() ?? false;
    }

    function getInitialSeenAdvancedCarouselOptions(): boolean {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.seenAdvancedCarouselOptions?.getValue() ?? false;
    }

    function getInitialBonusPuzzlesSeen(): Set<number> {
        const initialSyncedState = getInitialSyncedState();
        const s: Set<number> = initialSyncedState.bonusPuzzlesSeen?.getValue() ?? new Set();
        return new Set(Array.from(s).filter((num) => !isNaN(num)));
    }

    function getInitialDarkModeSetting(): DarkModeSetting {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.darkModeSetting?.getValue() ?? DarkModeSetting.SYSTEM;
    }

    function getInitialNotificationsEnabled(): boolean {
        return initialLocalState.notificationsEnabled === 'true';
    }

    function getInitialSeenNotificationsSettings() {
        return initialLocalState.seenNotificationsSettings === 'true';
    }

    function getInitialNotificationTime() {
        const val = initialLocalState.notificationTime;
        const targetDate = new Date();
        if (val == null) {
            targetDate.setHours(10);
            targetDate.setMinutes(0);
        } else {
            const [hour, minute] = val.split(':').map(Number);
            targetDate.setHours(hour);
            targetDate.setMinutes(minute);
        }
        targetDate.setSeconds(0);
        targetDate.setMilliseconds(0);
        return targetDate;
    }

    function getInitialAskedForRating(): boolean {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.askedForRating?.getValue() ?? false;
    }

    function getInitialAskedForReview(): boolean {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.askedForReview?.getValue() ?? false;
    }

    function getInitialNumBonusPuzzlesUnlocked(): number {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.numBonusPuzzlesUnlocked?.getValue() ?? 0;
    }

    function getInitialSeenShareIncentive() {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.seenShareIncentive?.getValue() ?? false;
    }

    function getInitialSeenNativeAppPromotion() {
        const initialSyncedState = getInitialSyncedState();
        return initialSyncedState.seenNativeAppPromotion?.getValue() ?? false;
    }

    function parseSelectedPuzzleFromPath(path: string): SelectedPuzzle {
        const now = new TZDate(new Date(), TIME_ZONE);
        if (path === '/') {
            return new ArchiveSelector(now);
        }
        try {
            if (path.match(BONUS_URL)) {
                const bonusUrl = path.match(BONUS_URL)!;
                return new BonusSelector(parseInt(bonusUrl[1], 10));
            } else if (path.match(BETA_URL)) {
                const betaUrl = path.match(BETA_URL)!;
                const id = betaUrl[1];
                if (id === 'random') {
                    return new InternalSelector(null);
                } else {
                    return new InternalSelector(parseInt(id, 10));
                }
            } else if (path.match(SPECIAL_URL)) {
                return new SpecialSelector(parseInt(path.match(SPECIAL_URL)![1], 10));
            }
            const archiveUrl = path.match(ARCHIVE_URL)!;
            const year = parseInt(archiveUrl[1], 10);
            const month = parseInt(archiveUrl[2], 10);
            const day = parseInt(archiveUrl[3], 10);
            const date = new TZDate(year, month - 1, day, 0, 0, 0, 0, TIME_ZONE); // Will throw if the date is invalid
            return new ArchiveSelector(date);
        } catch (e) {
            return new ArchiveSelector(now);
        }
    }

    function getInitialSelectedPuzzle(): SelectedPuzzle {
        const path = window.location.pathname;
        const defaultSelector = new ArchiveSelector(new TZDate(Date.now(), TIME_ZONE));
        let selectedPuzzle = parseSelectedPuzzleFromPath(path);
        if (selectedPuzzle instanceof BonusSelector) {
            if (numBonusPuzzlesUnlocked < selectedPuzzle.id) {
                // User has not unlocked this bonus puzzle yet.
                selectedPuzzle = defaultSelector;
                flashNotification(setAccessingLockedBonusPuzzlePopUpVisible, 2000);
            }
        }
        if (selectedPuzzle instanceof ArchiveSelector) {
            if (isFuture(selectedPuzzle.date) || isBefore(selectedPuzzle.date, ZERO_DAY)) {
                // User is trying to access a future date.
                selectedPuzzle = defaultSelector;
                flashNotification(setAccessingInvalidDatePopUpVisible, 2000);
            }
        }
        return selectedPuzzle;
    }

    function getTriplitUrl(): string {
        if (selectedPuzzle instanceof ArchiveSelector) {
            return `${API_BASE_URL}/api/v1/triple/${selectedPuzzle.date.getFullYear()}/${
                selectedPuzzle.date.getMonth() + 1
            }/${selectedPuzzle.date.getDate()}`;
        } else if (selectedPuzzle instanceof BonusSelector) {
            return `${API_BASE_URL}/api/v1/triple/bonus/${selectedPuzzle.id}`;
        } else if (selectedPuzzle instanceof InternalSelector) {
            return `${API_BASE_URL}/api/v1/triple/${selectedPuzzle.id ?? 'random'}`;
        } else if (selectedPuzzle instanceof SpecialSelector) {
            return `${API_BASE_URL}/api/v1/triple/special/${selectedPuzzle.id}`;
        } else {
            throw new Error('Unknown selected puzzle type: ' + JSON.stringify(selectedPuzzle));
        }
    }

    async function recordVisit() {
        try {
            const response = await applyTimeout(
                fetch(
                    `${API_BASE_URL}/api/v1/event/visit?userId=${userId}&sessionId=${sessionId}&newUser=${isFirstVisit}&totalPlayed=${totalPlayed}&totalWins=${totalWins}&trafficSource=${
                        TrafficSource[getTrafficSource()]
                    }&consentGiven=${consentGiven}&clientVersion=${window.CLIENT_VERSION}`,
                    {
                        method: 'POST',
                    },
                ),
            );
            const { isGdpr } = await response.json();
            setIsGdpr(isGdpr);
        } catch (e) {
            console.error('Error recording visit:', e);
        }
    }

    function needsRefresh() {
        const now = new TZDate(new Date(), TIME_ZONE);
        return differenceInHours(now, LAST_REFRESH) >= 1;
    }

    function shouldRefresh(): boolean {
        return needsRefresh() && !runningAsNativeApp();
    }

    function refreshIfStale() {
        // We allow the app to be at most 1 hour stale.
        const reload = shouldRefresh();
        if (reload) {
            console.log('App is out of date. Refreshing...');
            window.location.reload();
        }
    }
    useEffect(() => {
        void getAppInfo().then((appInfo: AppInfo) => {
            setAppInfo(appInfo);
        });
    }, []);

    useEffect(() => {
        if (!runningAsNativeApp()) {
            return;
        }
        function handleDeepLink(url: string) {
            try {
                console.log('Handling deep link:', url);
                const parsedUrl = new URL(url);
                let fullPath: string;
                if (parsedUrl.protocol === 'triplit:') {
                    // triplit://archive/2025/6/1 → host = "archive", pathname = "/2025/6/1"
                    fullPath = `/${parsedUrl.host}${parsedUrl.pathname}`;
                } else {
                    // https://triplit.com/archive/2025/6/1 → pathname = "/archive/2025/6/1"
                    fullPath = parsedUrl.pathname;
                }
                setSelectedPuzzle(parseSelectedPuzzleFromPath(fullPath));
            } catch (err) {
                console.error('Error handling deep link:', err);
                Sentry.captureException(err);
            }
        }
        void App.getLaunchUrl().then((launch) => {
            if (launch?.url) {
                console.info('App launched with URL:', launch.url);
                handleDeepLink(launch.url);
            }
        });
        const eventualHandle = App.addListener('appUrlOpen', ({ url }) => handleDeepLink(url));
        // Clean up listener on unmount or re-render
        return () => {
            void eventualHandle.then((handle) => {
                return handle.remove();
            });
        };
    }, []);

    useEffect(() => {
        if (!runningAsNativeApp()) {
            return;
        }
        const eventualHandle = LocalNotifications.addListener(
            'localNotificationActionPerformed',
            (notificationAction: ActionPerformed) => {
                console.log('Notification action performed:', notificationAction);
                if (notificationAction.actionId === 'tap') {
                    // Ensure that today's puzzle is selected.
                    console.log("Selecting today's puzzle...");
                    setSelectedPuzzle(parseSelectedPuzzleFromPath('/'));
                }
            },
        );

        // Clean up listener on unmount or re-render
        return () => {
            void eventualHandle.then((handle) => {
                return handle.remove();
            });
        };
    }, []);

    useEffect(() => {
        void recordVisit();
    }, []);

    useEffect(() => {
        if (!runningAsNativeApp()) {
            return;
        }

        const pauseHandle = App.addListener('pause', () => {
            console.info(`App paused. Updating localStorage[${LAST_APP_PAUSE_TIME_KEY}].`);
            // App has been moved to the background.
            localStorage.setItem(LAST_APP_PAUSE_TIME_KEY, new Date().toISOString());
        });

        return () => {
            void pauseHandle.then((handle) => {
                return handle.remove();
            });
        };
    }, []);

    useEffect(() => {
        if (!runningAsNativeApp()) {
            return;
        }

        function waitUntilVisible(): Promise<void> {
            return new Promise((resolve) => {
                if (document.visibilityState === 'visible') return resolve();
                const handler = () => {
                    if (document.visibilityState === 'visible') {
                        document.removeEventListener('visibilitychange', handler);
                        resolve();
                    }
                };
                document.addEventListener('visibilitychange', handler);
            });
        }

        async function resetAfterLongPause() {
            const val = localStorage.getItem(LAST_APP_PAUSE_TIME_KEY);
            localStorage.removeItem(LAST_APP_PAUSE_TIME_KEY);
            if (val == null) {
                return;
            }
            const lastPauseTime = new Date(val);
            // App has been moved to the foreground.
            if (differenceInHours(new Date(), lastPauseTime) >= 3) {
                console.info('App resumed after a long time. Hard reloading the home page.');
                Sentry.addBreadcrumb({
                    category: 'app.lifecycle',
                    message: 'Forcing hard reload resuming after long pause',
                    level: 'info',
                    data: {
                        location: window.location.href,
                        lastPauseTime: localStorage.getItem('lastPauseTime'),
                        now: new Date().toISOString(),
                    },
                });
                // Reload the home screen rather than showing an archived Triplit.
                await waitUntilVisible();
                window.location.replace('/');
            } else {
                console.info('App resumed after a short time. No action needed.');
            }
        }
        void resetAfterLongPause();
        const resumeHandle = App.addListener('resume', resetAfterLongPause);

        return () => {
            void resumeHandle.then((handle) => {
                return handle.remove();
            });
        };
    }, []);

    useEffect(() => {
        function handleVisibilityChange(event) {
            if (document.visibilityState === 'visible') {
                void refreshIfStale();
            }
        }
        function handleFocus(event) {
            void refreshIfStale();
        }
        window.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    useEffect(() => {
        if (!userIsActive) {
            openInactivityModal();
        } else {
            closeInactivityModal();
        }
    }, [userIsActive]);

    async function setStateForSelectedTriple(triple: Triple) {
        console.info('Setting state for selected Triplit:', triple);
        // Ensure that the puzzle state is loaded in the puzzle index.
        const puzzleState = puzzleIndex.getPuzzleStateById(triple.id, loginSession.getUserId());
        // Merge the newly fetched triple with the existing puzzle state.
        const updatedPuzzleState = new PuzzleState({ ...puzzleState, triple });
        await puzzleIndex.createOrUpdate(updatedPuzzleState, loginSession.getUserId());
        setActiveTriplitId(triple.id);
        setActiveUserId(loginSession.getUserId());
        console.info('Done setting state for selected Triplit, puzzle should load now');
    }

    async function fetchTriplit() {
        if (selectedPuzzle instanceof ArchiveSelector) {
            const now = new TZDate(new Date(), TIME_ZONE);
            const selectedPuzzleDate = new TZDate(selectedPuzzle.date, TIME_ZONE);
            const calendarDate = CalendarDate.fromDate(selectedPuzzle.date);
            const puzzleState = puzzleIndex.getPuzzleStateByDate(calendarDate, loginSession.getUserId());
            if (differenceInCalendarDays(now, selectedPuzzleDate) >= 1 && puzzleState == null) {
                console.info(
                    `User is accessing an archived puzzle. Now=${now.toISOString()}, selected=${selectedPuzzleDate.toISOString()}`,
                );
                // This is a pro-level feature, so we gate access to it
                try {
                    track('archived_puzzle_accessed', getCustomEventData());
                    await subscriptionContext.ensureProLevel(PaywallTrigger.ARCHIVE_ACCESS);
                    console.info('Access check complete, moving on to fetch the puzzle');
                } catch (e) {
                    console.info("Access check failed, falling back to today's puzzle");
                    // Fallback to showing today's puzzle.
                    setSelectedPuzzle(new ArchiveSelector(now));
                    if (e instanceof UserCancellationError) {
                        flashNotification(setArchivedPuzzleAccessBlockedPopUpVisible, 3000);
                        return;
                    } else {
                        // This is unexpected. Fail
                        inGameNotificationsContext.displayNotification(
                            'Unexpected error verifying pro status. Please try again later.',
                            3000,
                        );
                        console.error('Unexpected error ensuring pro level from game:', e);
                        Sentry.captureException(e);
                    }
                }
            }
        }

        if (seenArchiveRewardAdPromo) {
            // In general, we want to pre-fetch ads as early as possible, but this can trigger the consent flow.
            // So if the user hasn't seen the announcment about ads in the archive yet, we allow the modal to handle
            // the pre-fetching flow.
            void adInterface.initializeIfNeeded();
        } else {
            // Pre-fetch will happen when the user closes the app updates modal.
            setShouldShowArchiveRewardAdPromo(true);
        }

        const fetchUrl = getTriplitUrl();
        let shownSomething = false;
        // If we haven't loaded anything very quickly, show the loading spinner.
        setTimeout(() => {
            // We rely on a local variable in the closure because the state variable
            // binding will not be updated.
            if (!shownSomething) {
                // Indicate to the user that we are loading the Triplit.
                setLoadingState(LoadingState.LOADING);
            }
        }, 250);
        // Use the cached triplit immediately to load quickly
        // if one is availablle.
        const cachedTriple = getTripleFromIndex();
        if (cachedTriple) {
            console.info('Found cached Triplit:', cachedTriple);
            void setStateForSelectedTriple(cachedTriple).then(() => {
                setLoadingState(LoadingState.LOADED);
            });
            shownSomething = true;
        }

        try {
            console.info('Fetching Triplit from:', fetchUrl);
            const response = await applyTimeout(window.fetch(fetchUrl));
            if (!response.ok) {
                throw new Error(`Received not ok response: ${response.status} ${response.statusText}`);
            }
            const json = await response.json();
            console.info('Done fetching Triplit');
            const newTriple = new Triple(json);
            void setStateForSelectedTriple(newTriple).then(() => {
                setLoadingState(LoadingState.LOADED);
            });
            shownSomething = true;
        } catch (e) {
            // See if we already have the Triplit cached.
            console.error('Error fetching Triplit:', e);
            Sentry.captureException(e);
            if (!shownSomething) {
                setLoadingState(LoadingState.ERROR);
            }
            shownSomething = true;
        }
    }

    function getTripleFromIndex(): Triple | undefined {
        if (selectedPuzzle instanceof ArchiveSelector) {
            const targetDate = CalendarDate.fromDate(selectedPuzzle.date);
            return puzzleIndex.getPuzzleStateByDate(targetDate, loginSession.getUserId())?.triple;
        }
        return undefined;
    }

    function formatIdForDate(date: Date): number {
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yy = String(date.getFullYear()).slice(-2);
        return Number(`${mm}${dd}${yy}`); // e.g., 51925 for May 19, 2025
    }

    async function scheduleTestNotification() {
        const scheduledAt = new Date(addSeconds(new TZDate(), 5));
        const id = Math.floor(Math.random() * 1000000); // Random ID for testing
        const messages = ['📅 New day, new puzzle. Your Triplit awaits.'];
        const newNotifications: LocalNotificationSchema[] = [];
        newNotifications.push({
            id,
            channelId: CHANNEL_ID,
            title: 'Triplit',
            body: messages[0],
            schedule: { at: scheduledAt },
            sound: '', // Use the default sound
        });
        const { display } = await LocalNotifications.checkPermissions();
        if (display !== 'granted') {
            const granted = await LocalNotifications.requestPermissions();
            if (granted.display !== 'granted') {
                flashNotification(setPermissionForNotificationsDeniedVisible, 3000);
                return;
            }
        }
        await LocalNotifications.schedule({ notifications: newNotifications });
        flashNotification(setNotificationsRescheduledPopUpVisible, 3000);
        console.log('Test notification scheduled:', newNotifications);
    }

    async function maybeScheduleNotifications(updatedTime: Date | null = null, obtainPermissions = false) {
        if (!runningAsNativeApp()) return;
        if (!notificationsEnabled && !obtainPermissions) {
            console.log('Notifications are disabled, skipping scheduling.');
            return;
        }

        const { display } = await LocalNotifications.checkPermissions();
        if (display !== 'granted') {
            if (obtainPermissions) {
                const granted = await LocalNotifications.requestPermissions();
                if (granted.display !== 'granted') {
                    console.warn('Notification permissions denied, skipping scheduling.');
                    return;
                }
            } else {
                console.warn('Notification permissions not obtained, skipping scheduling.');
                return;
            }
        }

        const now = new Date();
        const existing = await LocalNotifications.getPending();
        const existingIds = new Set(existing.notifications.map((n) => n.id));
        console.log('Existing notifications:', existing);

        const newNotifications: Array<LocalNotificationSchema> = [];

        for (let i = 1; i <= 14; i++) {
            const scheduledAt = new Date();
            scheduledAt.setDate(now.getDate() + i);
            scheduledAt.setHours((updatedTime ?? notificationTime).getHours());
            scheduledAt.setMinutes((updatedTime ?? notificationTime).getMinutes());
            scheduledAt.setSeconds(0);
            scheduledAt.setMilliseconds(0);

            const id = formatIdForDate(scheduledAt);
            if (existingIds.has(id)) continue; // Skip if already scheduled

            const messages = [
                '🚨 A new Triplit just dropped. Bring your A-game.',
                '🧘 Ready for some mind-yoga? Play today’s Triplit!',
                '🌀 Open your mind. Question your assumptions. Crush today’s Triplit.',
                '🏁 Will you set a new PR on today’s Triplit? Go for it!',
                '💡 Your daily brain boost is here: your Triplit is ready to play.',
                '🧩 Today’s Triplit is out – can you crack it?',
                '⚙️ Get your brain in gear – a new Triplit is here!',
                '🧠 Test your wits with today’s Triplit. You’ve got this.',
                '👍 Warm up those thumbs and brain cells! It’s Triplit time!',
                '🧠 Stay sharp! A new Triplit is ready for you.',
                '⏱️ Take five. You’ve earned it — today’s Triplit is waiting.',
                '💪 Flex those puzzle muscles — a fresh Triplit is out.',
                '🔥 Keep your streak alive! Today’s Triplit is waiting.',
                '🔤 Three new letters, three new clues: solve today’s Triplit!',
                '☕ You need a break! Treat yourself to today’s Triplit.',
                '📅 New day, new puzzle. Your Triplit awaits.',
            ];

            // We need a monotonically increasing counter which increments every day, so we can use the
            // so we can statelessly cycle through messages regardless of how frequently we reschedule notifications.
            // This rules out day of the month or year since these can reset in the middle of a cycle.
            // Use the number of days since epoch for this purpose.
            const offset = differenceInCalendarDays(scheduledAt, new Date(0)) % messages.length;

            newNotifications.push({
                id,
                title: 'Triplit',
                channelId: CHANNEL_ID,
                body: messages[offset],
                schedule: { at: scheduledAt },
                sound: '', // Use the default sound
            });
        }

        if (newNotifications.length > 0) {
            try {
                console.log('Scheduling notifications:', newNotifications);
                await LocalNotifications.schedule({ notifications: newNotifications });
                setNotificationsEnabled(true);
            } catch (error) {
                console.error('Failed to schedule notifications:', error);
                Sentry.captureException(error);
            }
        } else {
            console.log('No new notifications to schedule.');
        }
    }

    async function deleteAllNotifications() {
        // Delete all stored notifications and reschedule
        const existing = await LocalNotifications.getPending();
        if (existing.notifications.length === 0) {
            console.log('No stale notifications to delete');
            return;
        }
        console.log('Existing notifications to delete:', existing);
        return await LocalNotifications.cancel(existing);
    }

    async function rescheduleAllNotifications(time: Date | null = null) {
        if (!runningAsNativeApp()) {
            return;
        }
        await deleteAllNotifications();
        // Schedule new notifications at updated time
        console.log('Deleted all notifications, now rescheduling...');
        return await maybeScheduleNotifications(time, /* obtainPermissions= */ true);
    }

    function onboardExperiencedPlayer() {
        track('already_knows_how_to_play_clicked', getCustomEventData());
        setOnboardingExperiencedPlayer(true);
        closeWelcomeModal();
        openSettingsModal();
        flashNotification(setSettingsReviewPopUpVisible, 3000);
        // Assume they know about the carousel options and how the feedback system works.
        setSeenAdvancedCarouselOptions(true);
        setSeenFeedbackTutorial(true);
    }

    useEffect(() => {
        if (onboardingExperiencedPlayer && !settingsModalIsOpen) {
            void setupPuzzle();
            setOnboardingExperiencedPlayer(false);
        }
    }, [settingsModalIsOpen, onboardingExperiencedPlayer]);

    function runTutorial() {
        setActiveTriplitId(TUTORIAL_ID);
        setLoadingState(LoadingState.LOADED);
        setTutorialOnScreen(true);
        const preGameDriver = createPreGameDriver(() => {
            setTutorialOnScreen(false);
        });
        preGameDriver.drive();
    }

    async function setupPuzzle(): Promise<void> {
        void refreshIfStale();
        // setLoadingState(LoadingState.LOADING);

        if (isFirstVisit && firstRender) {
            // Use the practice Triplit as an initial placeholder.
            setActiveTriplitId(TUTORIAL_ID);
            setLoadingState(LoadingState.LOADED);
            return;
        }

        // Tutorial flow
        if (tutorialMode) {
            runTutorial();
            return;
        }

        // Ordinary flow
        if (selectedPuzzle.targetPath() !== parseSelectedPuzzleFromPath(window.location.pathname).targetPath()) {
            history.pushState(
                selectedPuzzle.targetPath() + window.location.search,
                '',
                selectedPuzzle.targetPath() + window.location.search,
            );
        }
        await fetchTriplit();
    }

    useLayoutEffect(() => {
        const innerHandler = (systemIsDark: boolean) => {
            const themeColorMetas = document.querySelectorAll('meta[name="theme-color"]');
            const useDarkMode =
                darkModeSetting === DarkModeSetting.SYSTEM ? systemIsDark : darkModeSetting === DarkModeSetting.DARK;
            // We set the theme color explicitly rather than relying on the conditionally defined
            // CSS variable because there seems to be no guarantee about the timing of the CSS variable
            // update.
            if (useDarkMode) {
                document.documentElement.setAttribute('data-theme', 'dark');
                themeColorMetas.forEach((meta) => {
                    meta.setAttribute('content', getStyleValue('--body-background-color-dark'));
                });
                setDarkMode(true);
                if (runningAsNativeApp()) {
                    SafeArea.enable({
                        config: {
                            statusBarContent: 'light',
                            navigationBarContent: 'light',
                        },
                    }).catch(console.error);
                    StatusBar.setStyle({ style: Style.Dark }).catch(console.error);
                    StatusBar.setBackgroundColor({ color: getStyleValue('--body-background-color-dark') }).catch(
                        console.error,
                    );
                    if (Capacitor.getPlatform() === 'android') {
                        NavigationBar.setNavigationBarColor({
                            color: getStyleValue('--body-background-color-dark'),
                            darkButtons: false,
                        }).catch(console.error);
                    }
                }
            } else {
                document.documentElement.removeAttribute('data-theme');
                themeColorMetas.forEach((meta) => {
                    meta.setAttribute('content', getStyleValue('--body-background-color-light'));
                });
                setDarkMode(false);
                if (runningAsNativeApp()) {
                    SafeArea.enable({
                        config: {
                            statusBarContent: 'dark',
                            navigationBarContent: 'dark',
                        },
                    }).catch(console.error);
                    StatusBar.setStyle({ style: Style.Light }).catch(console.error);
                    StatusBar.setBackgroundColor({ color: getStyleValue('--body-background-color-light') })
                        .catch()
                        .catch(console.error);
                    if (Capacitor.getPlatform() === 'android') {
                        NavigationBar.setNavigationBarColor({
                            color: getStyleValue('--body-background-color-light'),
                            darkButtons: true,
                        }).catch(console.error);
                    }
                }
            }
        };
        const outerHandler = (e: MediaQueryListEvent) => {
            if (Capacitor.getPlatform() === 'android') {
                // This is a small hack on Android to ensure that we redraw
                // the status bar icons with the correct color on dark mode transitions.
                setTimeout(() => {
                    innerHandler(media.matches); // Ensure we set the initial state
                }, 1000);
            } else {
                innerHandler(e.matches);
            }
        };

        const media = window.matchMedia('(prefers-color-scheme: dark)');
        media.addEventListener('change', outerHandler);
        innerHandler(media.matches);
        if (isInitialRender.current) {
            if (Capacitor.getPlatform() === 'android') {
                // This is a small hack on Android to ensure that we redraw
                // the status bar icons with the correct color on cold starts.
                setTimeout(() => {
                    innerHandler(media.matches); // Ensure we set the initial state
                }, 2000);
            }
            isInitialRender.current = false; // Prevent this from running again
        }

        return () => {
            media.removeEventListener('change', outerHandler); // ✅ cleanup
        };
    }, [darkModeSetting]);

    useEffect(() => {
        const firstFailureFlagKey = 'reload-for-kv-store-primary';
        const secondFailureFlagKey = 'reload-for-kv-store-secondary';
        const interval = setInterval(() => {
            void store.isHealthy().then((healthy) => {
                if (!healthy) {
                    if (!localStorage.getItem(firstFailureFlagKey)) {
                        // If we have not just reloaded for the sake of getting the store healthy,
                        // set a flag so we don't keep reloading and then reload.
                        console.warn('Key-value store is unhealthy, reloading...');
                        localStorage.setItem(firstFailureFlagKey, 'true');
                        window.location.reload();
                    } else {
                        if (!localStorage.getItem(secondFailureFlagKey)) {
                            console.error(
                                'Key-value store is still unhealthy, but we just reloaded. Not reloading again.',
                            );
                            Sentry.captureMessage('Key-value store still unhealthy after reload');
                            alert(
                                `You can continue to play, but we are unable to save your progress. Please try quitting and reopening ${
                                    runningAsNativeApp() ? 'the app' : 'your browser'
                                }.`,
                            );
                            localStorage.setItem(secondFailureFlagKey, 'true');
                        } else {
                            console.warn('Key-value store is still unhealthy. We have already warned the user.');
                        }
                        setLocalPersistenceIssues(true);
                    }
                } else {
                    console.info('Key-value store is healthy.');
                    setLocalPersistenceIssues(false);
                    localStorage.removeItem(firstFailureFlagKey); // Clear the flag if store is healthy
                    localStorage.removeItem(secondFailureFlagKey); // Clear the secondary flag if store is healthy
                }
            });
        }, 10000);

        return () => {
            clearInterval(interval); // Clear the interval on unmount
        };
    }, []);

    useEffect(() => {
        let maybeSetupChannel = Promise.resolve();
        if (Capacitor.getPlatform() === 'android') {
            maybeSetupChannel = LocalNotifications.createChannel({
                id: CHANNEL_ID,
                name: 'Default Channel',
                importance: 5, // 5 = HIGH (shows heads-up)
            })
                .then(() => {
                    console.log('Successfully created notification channel:', CHANNEL_ID);
                })
                .catch((e) => {
                    console.error('Error creating notification channel:', e);
                    Sentry.captureException(e);
                });
        }
        void maybeSetupChannel.then(() => {
            // Do not obtain permissions here if the user has not yet granted them to
            // avoid asking for permissions on cold start. This is just meant to be a background
            // update.
            void maybeScheduleNotifications(/* updatedTime= */ null, /* obtainPermissions= */ false);
        });
    }, []);

    useEffect(() => {
        track(`visit_${isFirstVisit ? 'new' : 'existing'}_user`, getCustomEventData());
    }, []);

    function reinitializeGameWideState() {
        // Reinitialize the global state from the login session.
        setLastVictoryDate(getInitialLastVictoryDate());
        setStreakLength(getInitialStreakLength());
        setMaxStreakLength(getInitialMaxStreakLength());
        setTotalPlayed(getInitialTotalPlayed());
        setTotalWins(getInitialTotalWins());
        setTotalLosses(getInitialTotalLosses());
        setScoreDistribution(getInitialScoreDistribution());
        setDifficultyLevel(getInitialDifficultyLevel());
        setTimerEnabled(getInitialTimerEnabled());
        setSessionId(getInitialSessionId);
        setConsentGiven(getInitialConsentGiven());
        setUserId(getInitialUserId);
        setSessionIdCreationDate(getInitialSessionIdCreationDate());
        setSeenFeedbackTutorial(getInitialSeenFeedbackTutorial());
        setSeenAdvancedCarouselOptions(getInitialSeenAdvancedCarouselOptions());
        setNumBonusPuzzlesUnlocked(getInitialNumBonusPuzzlesUnlocked());
        setSeenShareIncentive(getInitialSeenShareIncentive());
        setBonusPuzzlesSeen(getInitialBonusPuzzlesSeen());
        setDarkModeSetting(getInitialDarkModeSetting());
        setSeenNativeAppPromotion(getInitialSeenNativeAppPromotion());
        setAskedForRating(getInitialAskedForRating());
        setAskedForReview(getInitialAskedForReview);
    }

    useEffect(() => {
        setFirstRender(false);
    }, []);
    useEffect(() => {
        window.history.replaceState(
            selectedPuzzle.targetPath() + window.location.search,
            '',
            selectedPuzzle.targetPath() + window.location.search,
        );
        const handler = function (e: PopStateEvent) {
            if (e?.state == null) {
                return;
            }
            setSelectedPuzzle(parseSelectedPuzzleFromPath((e.state as string).split('?')[0]));
        };
        window.addEventListener('popstate', handler);
        return () => {
            window.removeEventListener('popstate', handler);
        };
    }, []);

    useEffect(() => {
        console.info('Modal states changed. paywallIsOpen: ', subscriptionContext.paywallIsOpen);
        setModalIsOpen(isModalOpen());
    }, [...MODAL_STATES]);

    function isModalOpen() {
        return MODAL_STATES.some(Boolean);
    }

    function persistInitialGlobalState() {
        persistDifficultyLevel();
        // Do not update isFirstVisit in-memory state.
        // Next time we load the page, isFirstVisit will be false.
        void store.setValue('isFirstVisit', false.toString());
        setLastVisit(new Date());
    }

    useEffect(() => {
        persistInitialGlobalState();
        if (isFirstVisit) {
            // We slightly delay opening the welcome modal to ensure the browser doesn't sample
            // the transparent overlay and mix into the theme colors.
            requestAnimationFrame(() => {
                openWelcomeModal();
            });
        }
    }, []);

    useEffect(() => {
        if (!loginSession.sessionValid) {
            inGameNotificationsContext.displayNotification(
                'Please sign out and sign in again to resume syncing.',
                3000,
            );
        }
    }, [loginSession.sessionValid]);

    // Persisted-state related effects
    useEffect(persistDifficultyLevel, [difficultyLevel, loginSession, puzzleIndex]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('lastVisit', lastVisit, loginSession.getUserId());
    }, [store, lastVisit, puzzleIndex, loginSession]);
    useEffect(() => {
        if (maxStreakLength != null) {
            void puzzleIndex.setGameWideField('maxStreakLength', maxStreakLength, loginSession.getUserId());
        }
    }, [store, maxStreakLength, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('difficultyLevel', difficultyLevel, loginSession.getUserId());
    }, [store, difficultyLevel, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('timerEnabled', timerEnabled, loginSession.getUserId());
    }, [loginSession, puzzleIndex, store, timerEnabled]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('sessionId', sessionId, loginSession.getUserId());
    }, [store, sessionId, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('userId', userId, loginSession.getUserId());
    }, [loginSession, puzzleIndex, store, userId]);
    useEffect(() => {
        if (consentGiven != null) {
            void puzzleIndex.setGameWideField('consentGiven', consentGiven, loginSession.getUserId());
        }
    }, [store, consentGiven, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('sessionIdCreationDate', sessionIdCreationDate, loginSession.getUserId());
    }, [store, sessionIdCreationDate, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('seenFeedbackTutorial', seenFeedbackTutorial, loginSession.getUserId());
    }, [store, seenFeedbackTutorial, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField(
            'seenAdvancedCarouselOptions',
            seenAdvancedCarouselOptions,
            loginSession.getUserId(),
        );
    }, [store, seenAdvancedCarouselOptions, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('bonusPuzzlesSeen', bonusPuzzlesSeen, loginSession.getUserId());
    }, [store, bonusPuzzlesSeen, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('seenShareIncentive', seenShareIncentive, loginSession.getUserId());
    }, [store, seenShareIncentive, puzzleIndex, loginSession]);
    useEffect(() => {
        void store.setValue('seenNotificationsSettings', seenNotificationsSettings.toString());
    }, [store, seenNotificationsSettings]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('seenNativeAppPromotion', seenNativeAppPromotion, loginSession.getUserId());
    }, [store, seenNativeAppPromotion, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('darkModeSetting', darkModeSetting, loginSession.getUserId());
    }, [store, darkModeSetting, puzzleIndex, loginSession]);
    useEffect(() => {
        void store.setValue('notificationsEnabled', notificationsEnabled.toString());
    }, [store, notificationsEnabled, puzzleIndex, loginSession]);
    useEffect(() => {
        void store.setValue('notificationTime', dateToTimeString(notificationTime));
    }, [store, notificationTime, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('askedForRating', askedForRating, loginSession.getUserId());
    }, [store, askedForRating, puzzleIndex, loginSession]);
    useEffect(() => {
        void puzzleIndex.setGameWideField('askedForReview', askedForReview, loginSession.getUserId());
    }, [store, askedForReview, puzzleIndex, loginSession]);
    useEffect(() => {
        void store.setValue('seenArchiveRewardAdPromo', seenArchiveRewardAdPromo ? 'true' : 'false');
    }, [store, seenArchiveRewardAdPromo]);

    // Declare this effect AFTER the effects above to ensure all local changes have been flushed
    // to the index before re-initializing game-wide state from the index.
    useEffect(() => {
        reinitializeGameWideState();
    }, [syncVersion]);

    useEffect(() => {
        reinitializeGameWideState();

        async function setupThenHideSplash() {
            try {
                // Show the splash screen for at least 1 second, even if the puzzle is ready sooner.
                await Promise.all([setupPuzzle(), new Promise((resolve) => setTimeout(resolve, 1000))]);
            } finally {
                if (runningAsNativeApp()) {
                    void SplashScreen.hide();
                }
            }
        }
        console.log('Calling setupPuzzle because selectedPuzzle changed...');
        console.log('Selected puzzle is now:', JSON.stringify(selectedPuzzle));
        void setupThenHideSplash();
    }, [selectedPuzzle, tutorialMode, loginSession.session]);

    function getWinPercentage() {
        if (totalPlayed > 0) {
            return ((totalWins / totalPlayed) * 100).toFixed(0);
        } else {
            return '--';
        }
    }

    function unlockBonusPuzzle() {
        void puzzleIndex.recordBonusPuzzleUnlock(loginSession.getUserId());
        setNumBonusPuzzlesUnlocked((numBonusPuzzlesUnlocked) =>
            Math.min(NUM_BONUS_PUZZLES, numBonusPuzzlesUnlocked + 1),
        );
        if (numBonusPuzzlesUnlocked < NUM_BONUS_PUZZLES) {
            flashNotification(setBonusPuzzleUnlockedPopupVisible, 3000);
        }
    }

    async function handleShare(): Promise<void> {
        track('share', getCustomEventData());
        if (!shareData?.files || shareData.files.length === 0) {
            inGameNotificationsContext.displayNotification(
                'There was a problem generating the share image, please try again later.',
                3000,
            );
            Sentry.captureEvent({
                message: 'Share invoked but no share data available',
                level: 'error',
            });
            return;
        }
        const imageAccolade = shareData.files[0];
        const putAccoladeInClipboard = () => {
            const item = new ClipboardItem({ [imageAccolade.type]: imageAccolade });
            void navigator.clipboard.write([item]).then(() => {
                setClipboardMessagePopUpVisible(true);
                setTimeout(() => {
                    setClipboardMessagePopUpVisible(false);
                    unlockBonusPuzzle();
                }, 1500);
            });
        };

        function uint8ToBase64(buffer: Uint8Array): string {
            let binary = '';
            for (let i = 0; i < buffer.length; i++) {
                binary += String.fromCharCode(buffer[i]);
            }
            return btoa(binary);
        }

        if (runningAsNativeApp()) {
            const { title, text, files } = shareData;

            let shareUrl: string | undefined = undefined;

            if (files?.length) {
                const file = files[0];
                const arrayBuffer = await file.arrayBuffer();
                const buffer = new Uint8Array(arrayBuffer);
                const base64 = uint8ToBase64(buffer);

                const filePath = file.name;
                await Filesystem.writeFile({
                    path: filePath,
                    data: base64,
                    directory: Directory.Cache,
                });

                const { uri } = await Filesystem.getUri({
                    path: filePath,
                    directory: Directory.Cache,
                });

                shareUrl = uri;
            }

            Share.share({
                title,
                text,
                ...(shareUrl && { url: shareUrl }),
            })
                .then(unlockBonusPuzzle)
                .catch((error) => {
                    if (error?.message === 'Share canceled' || error?.errorMessage === 'Share canceled') {
                        console.log('User canceled sharing (Capacitor)');
                    } else {
                        console.error('Unexpected sharing error:', error);
                        Sentry.captureException(error);
                    }
                });
        } else {
            const testShareData: ShareData = {
                files: shareData.files,
            };
            if (isMobile() && window.navigator.canShare(testShareData) && window.navigator.canShare(shareData)) {
                window.navigator
                    .share(shareData)
                    .then(() => {
                        unlockBonusPuzzle();
                    })
                    .catch((error) => {
                        if (error instanceof DOMException && error.name === 'AbortError') {
                            console.log('User canceled sharing (Web)');
                        } else {
                            // The user intended to share but something else went wrong.
                            // Fallback to using the clipboard.
                            putAccoladeInClipboard();
                        }
                    });
            } else {
                putAccoladeInClipboard();
            }
        }
    }

    function toDifficultyLabel(difficultyLevel: DifficultyLevel) {
        switch (difficultyLevel) {
            case DifficultyLevel.EASY:
                return 'Easy';
            case DifficultyLevel.NORMAL:
                return 'Normal';
            case DifficultyLevel.HARD:
                return 'Hard';
            default:
                return 'undefined';
        }
    }

    async function getAppInfo(): Promise<AppInfo | undefined> {
        if (runningAsNativeApp()) {
            try {
                return await App.getInfo();
            } catch (error) {
                console.error('Error getting app info:', error);
                return undefined;
            }
        } else {
            return undefined;
        }
    }

    function onCarouselTap(numNonTourCarouselTaps: number) {
        if (numNonTourCarouselTaps >= 3 && !seenAdvancedCarouselOptions && canLaunchTutorial() && !tutorialOnScreen) {
            setSeenAdvancedCarouselOptions(true);
            setTutorialOnScreen(true);
            createAdvancedCarouselOptionsDriver(gameDifficultyLevel!, () => setTutorialOnScreen(false)).drive();
        }
    }
    const handleGameStateChange = useCallback((gameState) => {
        setGameState(gameState);
    }, []);

    async function parseAndSetNotificationTime(timeString: string) {
        const localTime = new Date();
        if (!timeString) {
            localTime.setHours(10, 0, 0, 0);
        } else {
            const [hours, minutes] = timeString.split(':').slice(0, 2).map(Number);
            localTime.setHours(hours, minutes, 0, 0);
        }
        setNotificationTime(localTime);
        // Ensure we use the time that was just specified
        await rescheduleAllNotifications(localTime);
        flashNotification(
            timeString ? setNotificationsRescheduledPopUpVisible : setInvalidNotificationTimePopUpVisible,
            3000,
        );
    }

    return (
        <div className='outer-container'>
            <div className='banner-top'>
                <a className='game-link' href='/'>
                    <div className='game-title'>Triplit</div>
                </a>
                <div className='menu-buttons-container'>
                    {localPersistenceIssues ||
                        (syncStatus === SyncStatus.ERROR && (
                            <SyncProblemsIcon className='banner-icon sync-issues-icon' />
                        ))}
                    {syncStatus === SyncStatus.SYNCING && (
                        <div className='banner-icon-container' aria-label='sync-icon' style={{ width: '24px' }}>
                            <AnimatedSyncIcon />
                        </div>
                    )}
                    {timerEnabled && !tutorialMode && timeElapsed != null && (
                        <div
                            aria-label='timer'
                            className='timer'
                            style={{ width: `${formatTimeElapsed(timeElapsed).length - 1 + 0.3}ch` }}>
                            {formatTimeElapsed(timeElapsed)}
                        </div>
                    )}
                    {streakLength > 0 && (
                        <div className='streak-container'>
                            <div className='streak-count' data-testid='banner-streak-length'>
                                {streakLength}
                            </div>
                            <FlameIcon className='banner-icon flame-icon' />
                        </div>
                    )}
                    <div
                        className={`present-icon-container ${
                            numBonusPuzzlesUnlocked > bonusPuzzlesSeen.size ? 'unseen-bonus' : ''
                        }`}
                        aria-label='bonus-puzzles-button'
                        onClick={openBonusPuzzlesModal}>
                        <PresentIcon className={`banner-icon present-icon cursor-pointer `} />
                    </div>

                    <div
                        className='banner-icon-container cursor-pointer'
                        aria-label='calendar-button'
                        onClick={() => {
                            track('calendar_banner', getCustomEventData());
                            openDateSelectorModal();
                        }}>
                        <CalendarIcon className='banner-icon calendar-icon' />
                    </div>
                    <div
                        className='banner-icon-container cursor-pointer'
                        aria-label='results-button'
                        onClick={openResultsModal}>
                        <ResultsIcon className='banner-icon results-icon' />
                    </div>
                    <div
                        className='banner-icon-container cursor-pointer'
                        aria-label='help-button'
                        onClick={openHelpModal}>
                        <InfoIcon id='help-icon' className='banner-icon' />
                    </div>
                    <div
                        className='banner-icon-container cursor-pointer'
                        aria-label='settings-button'
                        onClick={openSettingsModal}>
                        <SettingsIcon className='banner-icon settings-icon' />
                    </div>
                    <div
                        className='banner-icon-container cursor-pointer'
                        aria-label='menu-button'
                        onClick={openMenuModal}>
                        <KebabIcon className='banner-icon hamburger-menu-icon' />
                    </div>
                </div>
            </div>
            {loadingState === LoadingState.LOADING ? (
                <div className='loading-container' data-testid='spinner-container' aria-label='Loading'>
                    <Spinner />
                </div>
            ) : loadingState === LoadingState.ERROR ? (
                <div className='error-modal-container'>
                    <h2>Error</h2>
                    <SignalDisconnectedIcon className='disconnected-icon' />
                    <p>You seem to be offline.</p>
                    <p>Please select a downloaded puzzle or reconnect to the internet.</p>
                    <div className='control-row'>
                        <button className='action-button game-button' onClick={() => setupPuzzle()}>
                            Refresh
                        </button>
                    </div>
                </div>
            ) : loadingState === LoadingState.LOADED ? (
                <Puzzle
                    activeTriplitId={activeTriplitId!}
                    activeUserId={activeUserId}
                    isFirstVisit={isFirstVisit}
                    sessionId={sessionId}
                    difficultyLevel={difficultyLevel}
                    modalIsOpen={modalIsOpen}
                    onWin={onWin}
                    onLoss={onLoss}
                    onAlreadyCompleted={onAlreadyCompleted}
                    timerEnabled={timerEnabled}
                    onTimeElapsed={(elapsed) => {
                        setTimeElapsed(elapsed);
                    }}
                    onCarouselTap={onCarouselTap}
                    onFirstTutorialGuessFilled={startMidGameDriver}
                    onIncorrectTurn={(turnType, partialSubmit) =>
                        startFirstIncorrectTurnDriver(turnType, partialSubmit)
                    }
                    tutorialOnScreen={tutorialOnScreen}
                    onGameStateChanged={handleGameStateChange}
                    onGameDifficultyLevelChanged={setGameDifficultyLevel}
                    selector={selectedPuzzle}
                    onTutorialExited={() => {
                        if (confirm('Are you sure you want to exit the tutorial?')) {
                            track('tutorial_hard_exited', getCustomEventData());
                            setTutorialMode(false);
                        }
                    }}
                />
            ) : null}
            <Modal
                isOpen={welcomeModalIsOpen}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        height: 'fit-content',
                        border: '4px solid var(--default-text-color)',
                        maxWidth: '290px',
                        width: '290px',
                        margin: 'auto auto',
                        boxSizing: 'border-box',
                        padding: '30px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <div className='welcome-modal-container'>
                    <div>
                        <h2 className='welcome-superheader'>Welcome to</h2>
                        <h1 className='welcome-header'>Triplit</h1>
                    </div>
                    <img src={darkMode ? PyramidLogoDark : PyramidLogo} className='pyramid-logo' alt='Pyramid Logo' />
                    <div className='welcome-description-container'>
                        <div className='welcome-description'>
                            <div className='welcome-line'>
                                <span className='welcome-left'>Find</span>
                                <span className='welcome-center'>three</span>
                                <span className='welcome-right'>words</span>
                            </div>
                            <div className='welcome-line'>
                                <span className='welcome-left'>from</span>
                                <span className='welcome-center'>three</span>
                                <span className='welcome-right'>letters</span>
                            </div>
                            <div className='welcome-line'>
                                <span className='welcome-left'>and</span>
                                <span className='welcome-center'>three</span>
                                <span className='welcome-right'>clues.</span>
                            </div>
                        </div>
                    </div>
                    <div className='learning-style-container'>
                        <button
                            id='take-tour-button'
                            className='action-button game-button learning-style-button cursor-pointer'
                            onClick={() => {
                                closeWelcomeModal();
                                setTutorialMode(true);
                                track('start_clicked', getCustomEventData());
                            }}>
                            Start
                        </button>
                    </div>
                    <div
                        className='adjustment-text-link tutorial-bypass-text'
                        role='button'
                        onClick={onboardExperiencedPlayer}>
                        Already know how to play?
                    </div>
                </div>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        padding: '0',
                        height: 'fit-content',
                        maxWidth: '300px',
                        margin: '0 auto',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='centered-modal'
                isOpen={inactivityModalIsOpen}>
                <div style={{ margin: '15px' }}>
                    <h2> Game Paused </h2>
                    <div className='center-text'>Still there? Tap anywhere to resume.</div>
                </div>
            </Modal>
            <Modal
                isOpen={menuIsOpen}
                // onAfterOpen={afterOpenModal}
                onRequestClose={closeMenuModal}
                overlayClassName='top-aligned-modal right-aligned-modal below-banner-modal'
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        height: 'fit-content',
                        maxWidth: '250px',
                        margin: '0px',
                        width: '20%',
                        minWidth: '200px',
                        padding: '0',
                        right: '0',
                        left: 'auto',
                        position: 'relative',
                        border: '1px solid var(--default-text-color)',
                        borderRadius: '0',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                // style={customStyles}
                contentLabel='Menu Modal'>
                <div className='hamburger-menu-container'>
                    {loginSession?.session?.user ? (
                        <div
                            className='hamburger-menu-item cursor-pointer'
                            role='button'
                            onClick={(e) => {
                                closeMenuModal();
                                setAccountModalIsOpen(true);
                            }}>
                            My Account
                        </div>
                    ) : (
                        <div
                            className='hamburger-menu-item cursor-pointer'
                            role='button'
                            onClick={async (e) => {
                                closeMenuModal();
                                try {
                                    await loginSession.getOrOpenSession();
                                } catch (error) {
                                    if (!(error instanceof UserCancellationError)) {
                                        console.error('Unexpected error during login:', error);
                                        Sentry.captureException(error);
                                    }
                                }
                            }}>
                            Sign in/Sign up
                        </div>
                    )}
                    <div
                        className='hamburger-menu-item cursor-pointer'
                        role='button'
                        onClick={(e) => {
                            closeMenuModal();
                            window.open(getEmailFeedbackURL(), '_blank');
                        }}>
                        Submit feedback
                    </div>
                    <div
                        className='hamburger-menu-item cursor-pointer'
                        role='button'
                        onClick={(e) => {
                            setTutorialMode((tutorialMode) => !tutorialMode);
                            closeMenuModal();
                        }}>
                        {tutorialMode ? 'Exit' : 'Run'} tutorial
                    </div>
                    <div
                        className='hamburger-menu-item cursor-pointer'
                        role='button'
                        onClick={(e) => {
                            closeMenuModal();
                            void Browser.open({ url: `${API_BASE_URL}/public/privacy.html` });
                        }}>
                        Privacy Policy
                    </div>
                    <div
                        className='hamburger-menu-item cursor-pointer'
                        role='button'
                        onClick={(e) => {
                            closeMenuModal();
                            openPrivacySettings();
                        }}>
                        Privacy Settings
                    </div>
                    {window.DEBUG_MODE && runningAsNativeApp() && (
                        <div
                            className='hamburger-menu-item cursor-pointer'
                            role='button'
                            onClick={(e) => {
                                closeMenuModal();
                                void scheduleTestNotification();
                            }}>
                            Schedule test notification
                        </div>
                    )}
                    <div
                        className='hamburger-menu-item'
                        onClick={(e) => {
                            closeMenuModal();
                            openWelcomeModal();
                        }}>
                        <div className='diagnostic-info-header'>App Info</div>
                        <div className='diagnostic-info'>
                            Environment: {window.ENVIRONMENT_NAME} <br />
                            Client version: {window.CLIENT_VERSION} <br />
                            Native version: {appInfo?.version ?? 'N/A'} <br />
                            Native build: {appInfo?.build ?? 'N/A'} <br />
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                overlayClassName='centered-modal'
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: 'var(--modal-max-width)',
                        height: 'fit-content',
                        margin: 'auto auto',
                        border: '2px solid var(--default-text-color)',
                        padding: '0.8rem',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                isOpen={victoryModalIsOpen}
                onAfterOpen={() => {
                    if (gameState === GameState.WON && !seenShareIncentive) {
                        setSeenShareIncentive(true);
                        const driver = createShareIncentiveDriver();
                        driver.drive();
                    }
                }}
                onRequestClose={closeVictoryModal}
                contentLabel='Victory Modal'>
                <div className='victory-modal-container'>
                    <CloseButton onClose={closeVictoryModal} />
                    <h2>Triplit</h2>
                    <h3>Statistics</h3>
                    <div className='statistics-section'>
                        <table>
                            <tbody>
                                <tr className='statistics-row'>
                                    <td className='statistics-container'>
                                        <div className='statistics-figure' data-testid='win-percentage'>
                                            {getWinPercentage()}
                                        </div>
                                    </td>
                                    <td className='statistics-container'>
                                        <div className='statistics-figure' data-testid='total-played'>
                                            {totalPlayed || '--'}
                                        </div>
                                    </td>
                                    <td className='statistics-container'>
                                        <div className='statistics-figure' data-testid='current-streak'>
                                            {streakLength}
                                        </div>
                                    </td>
                                    <td className='statistics-container'>
                                        <div className='statistics-figure' data-testid='longest-streak'>
                                            {maxStreakLength}
                                        </div>
                                    </td>
                                </tr>
                                <tr className='statistics-row'>
                                    <td className='statistics-container'>
                                        <div className='statistics-descriptor'>Win %</div>
                                    </td>
                                    <td className='statistics-container'>
                                        <div className='statistics-descriptor'>Played</div>
                                    </td>
                                    <td className='statistics-container'>
                                        <div className='statistics-descriptor'>Current Streak</div>
                                    </td>
                                    <td className='statistics-container'>
                                        <div className='statistics-descriptor'>Longest Streak</div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <h3>Score Distribution</h3>
                    <div className='score-distribution-container'>
                        <Histogram
                            bins={new Array(TOTAL_TURNS).fill(0).map((_, idx) => idx + 1)}
                            frequencies={scoreDistribution}
                            lastScore={score}
                        />
                    </div>
                    <div className='victory-buttons-container'>
                        {shareData != null ? (
                            <button
                                id='share-button'
                                className={`game-button victory-button cursor-pointer victory-button share-button-default ${scoreToCssClass(
                                    score!,
                                    /*fill= */ false,
                                )}`}
                                disabled={shareData == null}
                                onClick={handleShare}>
                                Share
                                <ShareIcon />
                            </button>
                        ) : (
                            ''
                        )}
                        <button
                            className={`game-button victory-button cursor-pointer ${scoreToCssClass(
                                score!,
                                /* fill= */ false,
                            )}`}
                            onClick={() => {
                                track('play_again', getCustomEventData());
                                openDateSelectorModal();
                                closeVictoryModal();
                            }}>
                            Play More
                            <CalendarIcon />
                        </button>
                        {VictoryModalCta(victoryModalOpenCount)}
                    </div>
                </div>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        padding: 0,
                        height: 'fit-content',
                        maxWidth: '300px',
                        margin: '0 auto',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='top-aligned-modal below-banner-modal'
                isOpen={dateSelectorOpen}
                onRequestClose={closeDateSelectorModal}>
                <div className='date-selector-modal-container'>
                    <CloseButton onClose={closeDateSelectorModal} />
                    <h2> Triplit Archive </h2>
                    <DateSelector
                        onChange={(value: Date) => {
                            track('date_selected', getCustomEventData({ date: value }));
                            if (tutorialMode) {
                                const confirmed = confirm(
                                    'Are you sure you want to exit the tutorial and play an archived puzzle?',
                                );
                                if (!confirmed) {
                                    return;
                                }
                                setTutorialMode(false);
                            }
                            setSelectedPuzzle(new ArchiveSelector(value));
                            closeDateSelectorModal();
                        }}
                        dateSelected={selectedPuzzle instanceof ArchiveSelector ? selectedPuzzle.date : null}
                    />
                </div>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '225px',
                        padding: '20px',
                        height: 'fit-content',
                        margin: 'auto auto',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='centered-modal'
                isOpen={practiceRoundCompleteModalIsOpen}
                onRequestClose={closePracticeRoundCompleteModal}>
                <h2 className='congrats-header'>Congratulations! 🎉</h2>
                <h3>You just finished your first Triplit!</h3>
                <p>
                    Too easy or too hard?{' '}
                    <a className='adjustment-text-link' onClick={openSettingsModal}>
                        Adjust
                    </a>{' '}
                    the difficulty.
                </p>
                <p>
                    Review the complete rules{' '}
                    <a onClick={openHelpModal} className='adjustment-text-link'>
                        here!
                    </a>
                </p>
                <p>
                    A new puzzle comes out every day at <b>midnight ET</b>!
                </p>
                <p>Now let&apos;s play today&apos;s!</p>
                <div className='play-button-container'>
                    <button
                        className='game-button play-button cursor-pointer'
                        onClick={closePracticeRoundCompleteModal}>
                        Play
                    </button>
                </div>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '270px',
                        padding: '20px',
                        height: 'fit-content',
                        margin: 'auto auto',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='centered-modal'
                isOpen={nativeAppPromotionModalIsOpen}
                onAfterOpen={() => {
                    track('native_app_promotion_modal_shown', getCustomEventData());
                }}
                onRequestClose={() => {
                    closeNativeAppPromotionModal();
                }}>
                <CloseButton onClose={closeNativeAppPromotionModal} />
                <h2 className='new-puzzle-notification-header'>Congrats! ⭐️</h2>
                <p className='center-text app-store-cta'>
                    <b>You crushed the puzzle.</b>
                </p>

                <p>
                    The <b>Triplit app</b> takes things to the next level:
                </p>
                <ul className='features-list' style={{ paddingLeft: '0.5rem' }}>
                    <li>
                        <div>✅</div>
                        <div>
                            <b>Offline play</b>: Great for planes and subways!
                        </div>
                    </li>
                    <li>
                        <div>✅</div>
                        <div>
                            <b>Notifications</b>: Never miss a day or break your streak.
                        </div>
                    </li>
                    <li>
                        <div>✅</div>
                        <div>
                            <b>Full screen</b>: Zero browser clutter.
                        </div>
                    </li>
                    <li>
                        <div>✅</div>
                        <div>
                            <b>Haptics</b>: Get a nice buzz when you solve.
                        </div>
                    </li>
                </ul>
                <p className='center-text app-store-cta'>
                    <a
                        href={getAppStoreWebLink()}
                        className='adjustment-text-link'
                        onClick={() => {
                            track('app_store_link_clicked_promo_plain_text', getCustomEventData());
                        }}>
                        <b>Get the app today!</b>
                    </a>
                </p>
                <a
                    href={getAppStoreWebLink()}
                    className='app-store-button'
                    aria-label='Download Triplit on the App Store'
                    onClick={() => {
                        track('app_store_link_clicked_promo_official_button', getCustomEventData());
                    }}>
                    {isIOS() ? <AppStoreButton /> : <PlayStoreButton />}
                </a>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '260px',
                        padding: '20px',
                        height: 'fit-content',
                        border: '2px solid black',
                        margin: 'auto auto',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='centered-modal'
                isOpen={notificationModalIsOpen}
                onRequestClose={() => {
                    closeNotificationModal();
                }}>
                <h2 className='new-puzzle-notification-header'>Congrats!</h2>
                <p>
                    <b>You&apos;re officially a Triplit player now 🎖️</b>
                </p>
                <p>A brand new puzzle drops every day.</p>
                <p>Choose a daily reminder time that works for you — and keep your streak alive!</p>
                <div className='notification-time-form-container'>
                    <div className='row'>
                        <label className='bold' htmlFor='notification-time'>
                            Remind me at:
                        </label>
                        <input
                            className='notification-time-input'
                            type='time'
                            id='notification-time'
                            name='notification-time'
                            value={dateToTimeString(notificationTime)}
                            onChange={(e) => {
                                track('notification_time_set_from_nudge', getCustomEventData());
                                void parseAndSetNotificationTime(e.target.value);
                            }}
                        />
                    </div>
                    <button
                        className='notification-confirm-button game-button play-button cursor-pointer'
                        onClick={async () => {
                            track('notifications_enabled_from_prompt', getCustomEventData());
                            await rescheduleAllNotifications();
                            closeNotificationModal();
                        }}>
                        Confirm
                    </button>
                    <div
                        role='button'
                        className='adjustment-text-link skip-notifications-link centered-text'
                        onClick={() => {
                            track('notifications_skipped_from_prompt', getCustomEventData());
                            closeNotificationModal();
                        }}>
                        Not now
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={helpIsOpen}
                onAfterOpen={afterOpenHelpModal}
                onRequestClose={closeHelpModal}
                // className='help-modal'
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: 'var(--modal-max-width)',
                        margin: 'auto auto',
                        height: 'fit-content',
                        maxHeight: '90%',
                        overflowY: 'auto',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='centered-modal'
                contentLabel='Help Modal'>
                <div className='help-modal'>
                    <CloseButton onClose={closeHelpModal} />
                    <h2>Rules</h2>
                    <p>
                        <b>Note</b>: If you&apos;re looking for a guided tutorial, click{' '}
                        <a
                            className='adjustment-text-link'
                            onClick={() => {
                                closeHelpModal();
                                setTutorialMode(true);
                            }}>
                            here
                        </a>
                        !
                    </p>
                    <p>
                        Wonder what the <b>badges</b> mean on the share image? Click{' '}
                        <a className='adjustment-text-link' onClick={openBadgeLegendModal}>
                            here
                        </a>
                        !
                    </p>
                    <ul>
                        <li>
                            You start with 3 letters and 3 clues in <b>random</b> order.
                        </li>
                        <li>
                            <b>Goal</b>: find a 4, 5, and 6 letter word stacked in a pyramid. Each word should fit a
                            unique clue, and contain <b>all</b> the letters from the row above it plus one <b>new</b>{' '}
                            letter.
                        </li>
                        <ul>
                            <li>
                                Words must <b>agree</b> with their clues in part of speech, form, and number.{' '}
                                <a className='adjustment-text-link' onClick={openAgreementExamplesModal}>
                                    Examples
                                </a>
                            </li>
                            <li>
                                Clues ending in a question mark (&quot;?&quot;) indicate <b>word play</b>.{' '}
                                <a className='adjustment-text-link' onClick={openWordplayExamplesModal}>
                                    Examples
                                </a>
                            </li>
                        </ul>
                        <li>
                            You must solve the puzzle in <b>5 turns or less</b>. On each turn, you can either reveal a
                            letter or submit some or all of the words.
                        </li>
                        <li>
                            Upon submitting, you will get <b>feedback</b> on which tiles are correct. Correct tiles get
                            light green background, and incorrect tiles get gray text.
                        </li>
                        <li>
                            Example:
                            <div>
                                <img className='feedback-example' src={feedbackExample} />
                            </div>
                            In this example, every tile is correct except the <b>M</b> and the <b>I</b>.
                        </li>
                        <li>
                            Upon completion of the puzzle, each tile&apos;s color corresponds to the turn in which you
                            solved it, going chromatically from blue on turn 1 to red on turn 5:
                            <div>
                                <div className='first-turn inline-square'></div>{' '}
                                <div className='second-turn inline-square'></div>{' '}
                                <div className='third-turn inline-square'></div>{' '}
                                <div className='fourth-turn inline-square'></div>{' '}
                                <div className='fifth-turn inline-square'></div>
                            </div>
                        </li>
                    </ul>
                    <div className='release-info'>New puzzles come out at midnight ET.</div>
                </div>
            </Modal>
            <Modal
                isOpen={badgeLegendIsOpen}
                onRequestClose={closeBadgeLegendModal}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '375px',
                        margin: 'auto auto',
                        height: 'fit-content',
                        overflowY: 'auto',
                        padding: '20px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <CloseButton onClose={closeBadgeLegendModal} />
                <div className='badge-legend-modal'>
                    <h3 className='center-text'>Badge Guide</h3>
                    <h4> Difficulty Level </h4>
                    <table>
                        <tbody>
                            <tr>
                                <td>🐣 </td> <td>Easy </td>
                            </tr>
                            <tr>
                                <td>🐦</td> <td>Medium </td>
                            </tr>
                            <tr>
                                <td>🐦‍🔥</td> <td>Hard </td>
                            </tr>
                        </tbody>
                    </table>
                    <h4> Solve Speed </h4>
                    <table>
                        <tbody>
                            <tr>
                                <td>⚡️</td> <td>Lightning-fast </td>
                            </tr>
                            <tr>
                                <td>🐢</td> <td>Turtle-like</td>
                            </tr>
                        </tbody>
                    </table>
                    <h4> Solve Order </h4>
                    <table>
                        <tbody>
                            <tr>
                                <td>🪜 </td> <td>4-5-6 &quot;ascending&quot; order </td>
                            </tr>
                            <tr>
                                <td> 🛝 </td> <td> 6-5-4 &quot;descending&quot; order </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Modal>
            <Modal
                isOpen={agreementExamplesIsOpen}
                onRequestClose={closeAgreementExamplesModal}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '375px',
                        margin: 'auto auto',
                        height: 'fit-content',
                        overflowY: 'auto',
                        padding: '10px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <CloseButton onClose={closeAgreementExamplesModal} />
                <div className='examples-modal'>
                    <h3> Word-Clue Agreement</h3>
                    <p>
                        Words must <b>agree</b> with their clues in part-of-speech, form, and tense:
                    </p>
                    <table>
                        <thead>
                            <tr>
                                <th>Clue</th>
                                <th>Type</th>
                                <th>Valid ✅</th>
                                <th>Invalid ❌</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>&quot;Untamed being&quot;</td>
                                <td>singular noun</td>
                                <td>BEAST</td>
                                <td>BEASTS, FERAL</td>
                            </tr>
                            <tr>
                                <td>&quot;Eats&quot;</td>
                                <td>present-tense verb</td>
                                <td>DINES</td>
                                <td>DINED, EDIBLE</td>
                            </tr>
                            <tr>
                                <td>&quot;Secure&quot;</td>
                                <td>adjective or verb</td>
                                <td>STABLE, FIX</td>
                                <td>SAFETY</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Modal>
            <Modal
                isOpen={wordplayExamplesIsOpen}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '375px',
                        margin: 'auto auto',
                        height: 'fit-content',
                        maxHeight: '90%',
                        overflowY: 'auto',
                        padding: '10px 5px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <CloseButton onClose={closeWordplayExamplesModal} />
                <div className='examples-modal wordplay-modal'>
                    <h3> Wordplay Clues </h3>
                    <p>
                        A <b>question mark</b> (&quot;?&quot;) at the end of a clue indicates <b>word play</b>. This
                        means the clue may <b>not</b> have its usual, everyday interpretation.
                    </p>
                    <table>
                        <thead>
                            <tr>
                                <th>Clue</th>
                                <th>Answer</th>
                                <th>Comment</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>&quot;Wine pairing?&quot;</td>
                                <td>DINE</td>
                                <td>A pairing of words: &quot;Wine and dine&quot;</td>
                            </tr>
                            <tr>
                                <td>&quot;Neck warmers?&quot;</td>
                                <td>MANES</td>
                                <td>A special property of long hair</td>
                            </tr>
                            <tr>
                                <td>&quot;Asked for documentation?&quot;</td>
                                <td>CARDED</td>
                                <td>An ID card is literally a form of documentation</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        height: 'fit-content',
                        width: '300px',
                        maxWidth: '350px',
                        padding: '40px 30px',
                        boxSizing: 'border-box',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='centered-modal'
                isOpen={settingsModalIsOpen}
                onRequestClose={closeSettingsModal}
                contentLabel='Settings Modal'>
                <div className='settings-modal-container'>
                    <h2>Settings</h2>
                    <CloseButton onClose={closeSettingsModal} />

                    <div className='settings-section'>
                        <h3> Difficulty </h3>
                        <div className='difficulty-settings-container'>
                            {Object.keys(DifficultyLevel)
                                .filter((key) => !isNaN(Number(DifficultyLevel[key])))
                                .map((localDifficultyLevel) => {
                                    const level = DifficultyLevel[localDifficultyLevel];
                                    return (
                                        <div className='difficulty-setting-container' key={level}>
                                            <input
                                                type='radio'
                                                className='cursor-pointer'
                                                defaultChecked={difficultyLevel === level}
                                                data-testid={`difficulty-level-${level}`}
                                                name='difficulty-level-setting'
                                                onChange={(e) => {
                                                    // onChange only fires for a radio button when it is selected,
                                                    // so we don't need to check the value.
                                                    setSelectedDifficultyLevel(level);
                                                }}
                                            />
                                            <label>{toDifficultyLabel(level)}</label>
                                        </div>
                                    );
                                })}
                        </div>
                        <div className='difficulty-level-description'>
                            {selectedDifficultyLevel === DifficultyLevel.EASY && (
                                <ul className='difficulty-list'>
                                    <li>
                                        Clues are <b>ordered</b>.
                                    </li>
                                    <li>
                                        Triplit letters are <b>visible</b>.
                                    </li>
                                </ul>
                            )}
                            {selectedDifficultyLevel === DifficultyLevel.NORMAL && (
                                <ul className='difficulty-list'>
                                    <li>
                                        Clues are in <b>random order</b>.
                                    </li>
                                    <li>
                                        Triplit letters are <b>visible</b>.
                                    </li>
                                </ul>
                            )}
                            {selectedDifficultyLevel === DifficultyLevel.HARD && (
                                <ul className='difficulty-list'>
                                    <li>
                                        Clues are in <b>random order</b>.
                                    </li>
                                    <li>
                                        Triplit letters are <b>hidden</b>.
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className='settings-section'>
                        <h3> Dark Mode </h3>
                        <div className='difficulty-settings-container'>
                            {Object.keys(DarkModeSetting)
                                .filter((key) => !isNaN(Number(DarkModeSetting[key])))
                                .map((darkModeOverride) => {
                                    const level = DarkModeSetting[darkModeOverride];
                                    return (
                                        <div className='difficulty-setting-container' key={level}>
                                            <input
                                                className='cursor-pointer'
                                                type='radio'
                                                defaultChecked={darkModeSetting === level}
                                                name='dark-mode-setting'
                                                onChange={(e) => {
                                                    // onChange only fires for a radio button when it is selected,
                                                    // so we don't need to check the value.
                                                    setDarkModeSetting(level);
                                                }}
                                            />
                                            <label>
                                                {DarkModeSetting[level].charAt(0) +
                                                    DarkModeSetting[level].slice(1).toLowerCase()}
                                            </label>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                    <div className='settings-section'>
                        <div className='timer-setting-container'>
                            <label className='timer-setting-header' htmlFor='timer-switch'>
                                Display timer
                            </label>
                            <div className='timer-slider-container'>
                                <Switch
                                    id='timer-switch'
                                    changeCallback={(e) => {
                                        track('display_timer', getCustomEventData({ timer_enabled: e.target.checked }));
                                        setTimerEnabled(e.target.checked);
                                    }}
                                    initialValue={timerEnabled}
                                />
                            </div>
                        </div>
                    </div>
                    {runningAsNativeApp() && (
                        <div className='settings-section'>
                            <div className='timer-setting-container'>
                                <label className='timer-setting-header' htmlFor='notifications-switch'>
                                    Enable notifications
                                </label>
                                <div className='timer-slider-container'>
                                    <Switch
                                        id='notifications-switch'
                                        changeCallback={(e) => {
                                            track(
                                                `notifications_${e.target.checked ? 'enabled' : 'disabled'}`,
                                                getCustomEventData({ timer_enabled: e.target.checked }),
                                            );
                                            setNotificationsEnabled(e.target.checked);
                                            if (e.target.checked) {
                                                void rescheduleAllNotifications();
                                            } else {
                                                void deleteAllNotifications();
                                            }
                                        }}
                                        initialValue={notificationsEnabled}
                                    />
                                </div>
                            </div>
                            {notificationsEnabled && (
                                <div className='settings-section'>
                                    <div className='timer-setting-container'>
                                        <label htmlFor='notification-time' className='notification-time-label'>
                                            Remind me at:
                                        </label>
                                        <input
                                            className='notification-time-input'
                                            type='time'
                                            id='notification-time'
                                            name='notification-time'
                                            value={dateToTimeString(notificationTime)}
                                            onChange={(e) => {
                                                track('notification_time_set_from_settings', getCustomEventData());
                                                void parseAndSetNotificationTime(e.target.value);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        position: 'relative',
                        maxWidth: '300px',
                        height: 'fit-content',
                        margin: '0 auto',
                        padding: '20px',
                        boxSizing: 'border-box',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='top-aligned-modal below-banner-modal'
                isOpen={bonusPuzzlesModalIsOpen}
                onRequestClose={closeBonusPuzzlesModal}
                contentLabel='Bonus Puzzles Modal'>
                <div className='bonus-puzzles-modal-container'>
                    <CloseButton onClose={closeBonusPuzzlesModal} />
                    <h2>Bonus Puzzles</h2>
                    <p>
                        Bonus puzzles unlocked:{' '}
                        <b>
                            {numBonusPuzzlesUnlocked}/{NUM_BONUS_PUZZLES}
                        </b>
                    </p>
                    <div className='bonus-puzzles-container'>
                        {Array.from({ length: 10 }, (_, index) => {
                            return (
                                <div
                                    className={`bonus-puzzle-cell ${
                                        !bonusPuzzlesSeen.has(index + 1) && numBonusPuzzlesUnlocked >= index + 1
                                            ? 'bonus-puzzle-unseen'
                                            : ''
                                    } ${
                                        selectedPuzzle instanceof BonusSelector && selectedPuzzle.id === index + 1
                                            ? 'bonus-selected'
                                            : ''
                                    } ${numBonusPuzzlesUnlocked <= index ? 'bonus-locked' : 'cursor-pointer'}`}
                                    key={index}
                                    onClick={() => {
                                        if (numBonusPuzzlesUnlocked > index) {
                                            setSelectedPuzzle(new BonusSelector(index + 1));
                                            setBonusPuzzlesSeen((bonusPuzzlesSeen) => {
                                                bonusPuzzlesSeen = new Set(bonusPuzzlesSeen);
                                                bonusPuzzlesSeen.add(index + 1);
                                                return bonusPuzzlesSeen;
                                            });
                                            closeBonusPuzzlesModal();
                                        }
                                    }}>
                                    {index + 1}
                                </div>
                            );
                        })}
                    </div>
                    {NUM_BONUS_PUZZLES - numBonusPuzzlesUnlocked > 0 && (
                        <p>Share with a friend to unlock another bonus puzzle!</p>
                    )}
                </div>
            </Modal>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '350px',
                        margin: 'auto auto',
                        padding: '20px',
                        boxSizing: 'border-box',
                        height: 'fit-content',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                isOpen={privacySettingsIsOpen}
                onRequestClose={closePrivacySettings}
                contentLabel='Privacy Settings Modal'>
                <CloseButton onClose={closePrivacySettings} />
                <h2 className='privacy-policy-settings-header'>Privacy Settings</h2>
                <p className='privacy-policy-settings-explanation'>
                    We collect anonymized data to improve Triplit. The data is strictly related to how users play the
                    game and is not joined with any other outside data sources. We will never share this data with
                    third-parties. You may opt out of this collection at any time.
                </p>
                <div className='privacy-policy-settings-container'>
                    <input
                        className='cursor-pointer'
                        type='checkbox'
                        checked={consentGiven}
                        onChange={(e) => setConsentGiven(e.target.checked)}
                    />
                    <label
                        className='cursor-pointer privacy-policy-label'
                        onClick={() => setConsentGiven(!consentGiven)}>
                        <p>Allow anonymized data collection</p>
                    </label>
                </div>

                {adInterface.adsSupported() && adInterface.privacyOptionsRequired() && (
                    <div className='privacy-options-button-container'>
                        <button
                            className='game-button action-button privacy-options-button'
                            onClick={() => {
                                void adInterface.showPrivacyOptions().catch((e) => {
                                    console.error('Error showing privacy options:', e);
                                    Sentry.captureException(e);
                                    inGameNotificationsContext.displayNotification(
                                        'There was a problem opening privacy settings. Please try again later.',
                                        3000,
                                    );
                                });
                            }}>
                            Manage other privacy settings
                        </button>
                    </div>
                )}
            </Modal>
            <ArchiveRewardAdPromo
                shouldBeOpen={shouldShowArchiveRewardAdPromo}
                onClose={() => {
                    void adInterface.initializeIfNeeded();
                    setSeenArchiveRewardAppPromo(true);
                    setShouldShowArchiveRewardAdPromo(false);
                }}
                onTryItNow={() => {
                    openDateSelectorModal();
                }}
            />
            <Account isOpen={accountModalIsOpen} onClose={() => setAccountModalIsOpen(false)} />
            <div className={`banner-popup ${bonusPuzzleUnlockedPopUpVisible ? '' : 'none'}`}>
                You have unlocked a bonus puzzle!
                <br />
                Tap <PresentIcon className='svg-as-text' /> in the banner to find it.
            </div>
            <div className={`banner-popup ${victoryMessagePopUpVisible ? '' : 'none'}`}>{getWordingForScore()}</div>
            <div className={`banner-popup ${difficultyLevelPopUpVisible ? '' : 'none'}`} style={{ zIndex: 10000 }}>
                This will take effect on your next Triplit.
            </div>
            <div className={`banner-popup ${settingsReviewPopUpVisible ? '' : 'none'}`} style={{ zIndex: 10000 }}>
                Please take a moment to review your settings.
            </div>
            <div className={`banner-popup ${accessingLockedBonusPuzzlePopUpVisible ? '' : 'none'}`}>
                You have not yet unlocked this bonus puzzle! Please share with more friends to unlock.
            </div>
            <div className={`banner-popup ${accessingInvalidDatePopUpVisible ? '' : 'none'}`}>
                You have entered an invalid date. Redirecting you to today&apos;s puzzle.
            </div>
            <div className={`banner-popup ${permissionForNotificationsDeniedVisible ? '' : 'none'}`}>
                Permission to send notifications has been denied.
            </div>
            <div className={`banner-popup ${notificationsRescheduledPopUpVisible ? '' : 'none'}`}>
                Notifications successfully scheduled
            </div>
            <div className={`banner-popup ${invalidNotificationTimePopUpVisible ? '' : 'none'}`}>
                You have entered an invalid time. The default time was used instead.
            </div>
            <div className={`banner-popup ${archivedPuzzleAccessBlockedPopUpVisible ? '' : 'none'}`}>
                Archived puzzles require Pro to access. Redirecting you to today&apos;s puzzle.
            </div>
            <div style={{ zIndex: 10000 }} className={`banner-popup ${clipboardMessagePopUpVisible ? '' : 'none'}`}>
                Copied results to clipboard
            </div>
            <div
                style={{ zIndex: 10000 }}
                className={`banner-popup ${accountUpgradeCanceledPopUpVisible ? '' : 'none'}`}>
                Account upgrade was canceled...
            </div>
            <div className='mobile-landscape-warning-container'>Please rotate your device to portrait mode.</div>
        </div>
    );
}
